import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import FileUpload from '../components/FileUpload';
import CollapsibleSection from '../components/CollapsibleSection';
import CompactProcessingStatus from '../components/CompactProcessingStatus';
import TranscriptViewer from '../components/TranscriptViewer';
import TranscriptList from '../components/TranscriptList';
import { AssessmentGenerator } from '../components/AssessmentGenerator';
import EventDetectionPanel from '../components/EventDetectionPanel';
import ProgressBar from '../components/ProgressBar';
import { BackgroundJobTimeline } from '../components/BackgroundJobTimeline';
import { useTranscription } from '../hooks/useTranscription';
import { useDeleteAssessments } from '../hooks/useDeleteAssessments';
import { formatDateISO, formatFileSize } from '../lib/dateUtils';
import { extractTextFromFile, getSupportedExtensions } from '../lib/textExtraction';
import { waitForTranscript, reconstructTranscriptWithSpeakerLabels } from '../lib/transcriptUtils';
import { SpeakerRoleAssignmentModal, type SpeakerRoleAssignment } from '../components/SpeakerRoleAssignmentModal';
import {
  isAudioFile,
  sortAudioFiles,
  getAudioTypeLabel,
  concatenateAudioServerSide,
  type ConcatenationProgress,
  type AudioFile,
} from '../lib/audioConcatenation';
import RecordButton from '../components/RecordButton';
import { useProgressiveTranscription } from '../hooks/useProgressiveTranscription';
import { COCKPIT_LANGUAGE_OPTIONS } from '../lib/constants';

interface SessionData {
  id: string;
  session_date: string;
  aircraft_type: string;
  flight_phase: string;
  scenario: string | null;
  instructor_notes: string | null;
  status: string;
  organization_id: string;
  trainee_id: string | null; // Nullable: mobile creates sessions via session_crew instead
  instructor_id: string; // Phase 2: Added for speaker role mapping
  cockpit_language: string | null; // Persisted cockpit 2nd language (e.g., 'nl', 'de')
  trainee: {
    full_name: string;
    email: string;
  } | null;
  instructor: {
    full_name: string;
  };
  airline?: {
    language_code: string | null;
  };
  created_at: string;
}

interface FileData {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_path: string;
  created_at: string;
}

interface RecordingData {
  id: string;
  session_id: string;
  phase: 'briefing' | 'simulator' | 'debriefing';
  storage_path: string;
  duration_seconds: number | null;
  file_size_bytes: number | null;
  status: string;
  created_at: string;
}

interface TranscriptData {
  id: string;
  session_id: string;
  file_id: string | null;
  word_count: number;
  duration_seconds: number | null;
  processing_status: string;
  created_at: string;
  is_archived?: boolean;
}

export default function SessionDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Ref for scrolling to event detection panel
  const eventDetectionRef = useRef<HTMLDivElement>(null);
  
  const [session, setSession] = useState<SessionData | null>(null);
  const [pilotCrew, setPilotCrew] = useState<Array<{ user_id: string; crew_role: 'captain' | 'first_officer'; full_name?: string }>>([]);
  const [files, setFiles] = useState<FileData[]>([]);
  const [recordings, setRecordings] = useState<RecordingData[]>([]);
  const [loading, setLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);

  // Audio playback state
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assessmentError, setAssessmentError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [transcripts, setTranscripts] = useState<TranscriptData[]>([]);
  const [selectedTranscriptId, setSelectedTranscriptId] = useState<string | null>(null);
  const [showTranscriptViewer, setShowTranscriptViewer] = useState(false);
  const [transcriptRefreshTrigger, setTranscriptRefreshTrigger] = useState(0);
  const [jobTimelineRefreshKey, setJobTimelineRefreshKey] = useState(0); // Trigger immediate refresh of BackgroundJobTimeline
  const [assessmentsGenerated, setAssessmentsGenerated] = useState({
    pilot: false,
    instructor: false,
  });

  // Track if span-based extraction was used (validation is verbatim)
  const [isValidationVerbatim, setIsValidationVerbatim] = useState(false);

  // Debriefing Guide state
  const [debriefingGuideStatus, setDebriefingGuideStatus] = useState<{
    exists: boolean;
    status: string | null;
    id: string | null;
  }>({ exists: false, status: null, id: null });

  // Debriefing Guide generation state (standalone button)
  const [generatingDebriefing, setGeneratingDebriefing] = useState(false);
  const [debriefingProgress, setDebriefingProgress] = useState(0);
  const [debriefingError, setDebriefingError] = useState<string | null>(null);

  // Instructor Assessment generation state (standalone button)
  const [generatingInstructorAssessment, setGeneratingInstructorAssessment] = useState(false);
  const [instructorAssessmentProgress, setInstructorAssessmentProgress] = useState(0);

  // Pilot Assessment generation state (standalone button in "Generate Additional" section)
  const [generatingPilotAssessment, setGeneratingPilotAssessment] = useState(false);

  // LocalStorage key for persisting instructor assessment generation state across navigation
  const INSTRUCTOR_GEN_KEY = `orca360_instructor_gen_${id}`;

  // MAD-17: Manual transcript upload state
  const [uploadingTranscript, setUploadingTranscript] = useState(false);
  const [transcriptUploadError, setTranscriptUploadError] = useState<string | null>(null);
  const [processingTranscriptId, setProcessingTranscriptId] = useState<string | null>(null); // NEW: Track which transcript is being processed

  // Phase 2: Speaker role management state
  const [showSpeakerRoleModal, setShowSpeakerRoleModal] = useState(false);
  const [speakerRoleAssignments, setSpeakerRoleAssignments] = useState<SpeakerRoleAssignment[]>([]);

  // Cockpit language for code switching (second language alongside English)
  // Initialized as null, then set from session/airline data after load (see useEffect below)
  const [secondLanguage, setSecondLanguage] = useState<string | null>(null);
  const [secondLanguageInitialized, setSecondLanguageInitialized] = useState(false);
  const [loadingSpeakerRoles, setLoadingSpeakerRoles] = useState(false);

  // Audio concatenation state (for multi-file transcription)
  const [concatenationProgress, setConcatenationProgress] = useState<ConcatenationProgress | null>(null);
  const [customFileOrder, setCustomFileOrder] = useState<AudioFile[] | null>(null); // Custom order for concatenation

  // Combine-and-retranscribe proposal (shown when 2nd audio file is uploaded)
  const [showCombineProposal, setShowCombineProposal] = useState(false);

  // Debriefing stitch proposal (shown when 3rd+ audio file is uploaded with existing transcript)
  const [showDebriefStitchProposal, setShowDebriefStitchProposal] = useState(false);
  const [latestAudioFile, setLatestAudioFile] = useState<FileData | null>(null);

  // P3: Voice Passport — tracks whether speaker profiles exist for this session
  const [hasVoicePassport, setHasVoicePassport] = useState(false);

  // Collapsible section state for accordion UI
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    sessionDetails: true,
    participants: true,
    sessionFiles: false, // open initially per design spec
    eventDetection: true,
    availableTranscripts: true,
    processingStatus: true,
  });

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Transcription hook
  const { transcriptionStatus, startTranscription } = useTranscription(id);

  // Progressive transcription hook
  const progressive = useProgressiveTranscription(id);

  // Delete assessments hook
  const { deleteAssessments, isDeleting, error: deleteError } = useDeleteAssessments();

  // Computed: Check if there are any completed (non-archived) transcripts
  // This is used to hide the Generate Reports steps during first-time transcription
  const hasCompletedTranscripts = transcripts.some(
    t => t.processing_status === 'completed' && !t.is_archived
  );

  const loadTranscripts = useCallback(async () => {
    if (!id) return;

    try {
      // Get ALL transcripts for this session (multi-transcript support)
      // Include all fields needed by TranscriptList component
      const { data, error } = await supabase
        .from('transcripts')
        .select('id, session_id, word_count, duration_seconds, processing_status, created_at, file_id, is_archived')
        .eq('session_id', id)
        .or('is_archived.is.null,is_archived.eq.false')
        .order('created_at', { ascending: false }); // Most recent first

      if (error) {
        console.log('[SessionDetails] No transcripts found:', error);
        setTranscripts([]);
      } else if (data && data.length > 0) {
        console.log(`[SessionDetails] Loaded ${data.length} transcript(s)`);
        setTranscripts(data);
        // Auto-select the most recent transcript if none selected
        if (!selectedTranscriptId && data.length > 0 && data[0]) {
          setSelectedTranscriptId(data[0].id);
        }
      } else {
        setTranscripts([]);
      }
    } catch (error) {
      // No transcript yet - this is normal
      console.error('[SessionDetails] Error loading transcripts:', error);
      setTranscripts([]);
    }
  }, [id]);

  const loadAssessments = useCallback(async () => {
    if (!id) return;

    try {
      // Check for pilot assessments
      const { data: pilotData, error: pilotError } = await supabase
        .from('assessments_pilot')
        .select('id, competency, score')
        .eq('session_id', id);

      if (pilotError) {
        console.error('[SessionDetails] Error loading pilot assessments:', pilotError);
      }

      // Check for instructor assessments
      const { data: instructorData, error: instructorError } = await supabase
        .from('assessments_instructor')
        .select('id, criterion, score')
        .eq('session_id', id);

      if (instructorError) {
        console.error('[SessionDetails] Error loading instructor assessments:', instructorError);
      }

      const hasAssessments = {
        pilot: (pilotData && pilotData.length > 0) || false,
        instructor: (instructorData && instructorData.length > 0) || false,
      };

      setAssessmentsGenerated(hasAssessments);
    } catch (error) {
      console.error('[SessionDetails] Error checking assessments:', error);
    }
  }, [id]);

  // Load debriefing guide status
  const loadDebriefingGuide = useCallback(async () => {
    if (!id) return;

    console.log('[SessionDetails] Loading debriefing guide for session:', id);

    // Get current auth state for debugging
    const { data: authData } = await supabase.auth.getUser();
    console.log('[SessionDetails] Current user auth state:', {
      userId: authData?.user?.id,
      email: authData?.user?.email,
      hasUser: !!authData?.user
    });

    try {
      // Don't use .single() - it throws error if no rows found
      const { data, error, status, statusText } = await supabase
        .from('debriefing_guides')
        .select('id, status')
        .eq('session_id', id)
        .order('version', { ascending: false })
        .limit(1);

      console.log('[SessionDetails] Debriefing guide query result:', {
        data,
        error,
        status,
        statusText,
        dataLength: data?.length,
        hasData: !!data && data.length > 0
      });

      if (error) {
        // Table might not exist yet (migration not run)
        if (error.message?.includes('relation') || error.message?.includes('does not exist')) {
          console.log('[SessionDetails] Debriefing guides table does not exist (migration 068 not applied)');
        } else {
          console.error('[SessionDetails] Error loading debriefing guide:', error);
          console.error('[SessionDetails] Error details:', {
            code: error.code,
            message: error.message,
            hint: error.hint,
            details: error.details
          });
        }
        setDebriefingGuideStatus({ exists: false, status: null, id: null });
      } else if (data && data.length > 0 && data[0]) {
        const guide = data[0];
        console.log('[SessionDetails] ✅ Debriefing guide found:', guide.id, 'status:', guide.status);
        setDebriefingGuideStatus({ exists: true, status: guide.status, id: guide.id });
      } else {
        console.log('[SessionDetails] ⚠️ No debriefing guide found for this session');
        console.log('[SessionDetails] This could mean:');
        console.log('  1. Guide was never generated (feature flag not set during assessment generation)');
        console.log('  2. RLS policy is blocking access (check if user has instructor_id or trainee_id on session)');
        console.log('  3. Guide exists but query filter is incorrect');
        setDebriefingGuideStatus({ exists: false, status: null, id: null });
      }
    } catch (error) {
      console.error('[SessionDetails] Exception loading debriefing guide:', error);
      setDebriefingGuideStatus({ exists: false, status: null, id: null });
    }
  }, [id]);

  // Debug: Log debriefing guide status changes
  useEffect(() => {
    console.log('[SessionDetails] Debriefing guide status changed:', debriefingGuideStatus);
  }, [debriefingGuideStatus]);

  // Handler to scroll to event detection panel (called from AssessmentGenerator warning dialog)
  const handleScrollToEventDetection = useCallback(() => {
    if (eventDetectionRef.current) {
      eventDetectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a highlight effect
      eventDetectionRef.current.classList.add('ring-2', 'ring-sky-500', 'ring-offset-2');
      setTimeout(() => {
        eventDetectionRef.current?.classList.remove('ring-2', 'ring-sky-500', 'ring-offset-2');
      }, 3000);
    }
  }, []);

  // Handler for standalone debriefing guide generation button
  const handleGenerateDebriefingGuide = async () => {
    if (!session?.id) return;

    setGeneratingDebriefing(true);
    setDebriefingProgress(0);
    setDebriefingError(null);

    // Simulate progress stages while API processes
    const progressInterval = setInterval(() => {
      setDebriefingProgress(prev => {
        if (prev < 90) return prev + 10;
        return prev;
      });
    }, 2000);

    try {
      console.log('[Debriefing] Starting standalone generation for session:', session.id);
      setDebriefingProgress(10);

      const { data: { session: authSession } } = await supabase.auth.getSession();
      const response = await fetch('/api/generate-debriefing-guide', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token}`,
        },
        body: JSON.stringify({ sessionId: session.id })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate debriefing guide');
      }

      setDebriefingProgress(100);

      if (result.alreadyExists) {
        console.log('[Debriefing] Guide already exists');
      } else {
        console.log('[Debriefing] Guide generated successfully:', result.guideId);
        console.log('[Debriefing] Processing time:', result.processingTimeMs, 'ms');
      }

      // Reload debriefing guide status
      await loadDebriefingGuide();
    } catch (error) {
      console.error('[Debriefing] Generation failed:', error);
      setDebriefingError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      clearInterval(progressInterval);
      setGeneratingDebriefing(false);
      setDebriefingProgress(0);
    }
  };

  // Phase 2: Load speaker role assignments
  const loadSpeakerRoles = useCallback(async () => {
    if (!id) return;

    setLoadingSpeakerRoles(true);
    try {
      const { data, error } = await supabase
        .from('speaker_role_mappings')
        .select('speaker_label, speaker_role, user_id, confidence, manual_override')
        .eq('session_id', id)
        .order('speaker_label', { ascending: true });

      if (error) {
        // Check if it's a "table doesn't exist" error (migration not run yet)
        if (error.message?.includes('relation') && error.message?.includes('does not exist')) {
          console.log('[SessionDetails] Speaker role mappings table not found (migration 050 not applied yet)');
        } else {
          console.error('[SessionDetails] Error loading speaker roles:', error);
        }
        setSpeakerRoleAssignments([]);
      } else if (data && data.length > 0) {
        console.log(`[SessionDetails] Loaded ${data.length} speaker role assignment(s)`);
        setSpeakerRoleAssignments(data as SpeakerRoleAssignment[]);
      } else {
        console.log('[SessionDetails] No speaker role assignments found');
        setSpeakerRoleAssignments([]);
      }
    } catch (error: any) {
      // Silently handle "table doesn't exist" errors for backward compatibility
      if (error?.message?.includes('relation') || error?.message?.includes('does not exist')) {
        console.log('[SessionDetails] Speaker role feature not available yet (requires migration 050)');
      } else {
        console.error('[SessionDetails] Error loading speaker roles:', error);
      }
      setSpeakerRoleAssignments([]);
    } finally {
      setLoadingSpeakerRoles(false);
    }

    // P3: Check if voice passport exists for this session
    try {
      const { data: passport, error: passportError } = await supabase
        .from('session_speaker_map')
        .select('id')
        .eq('session_id', id)
        .limit(1);

      if (!passportError && passport && passport.length > 0) {
        setHasVoicePassport(true);
      } else {
        setHasVoicePassport(false);
      }
    } catch {
      // Table may not exist yet — silently ignore
      setHasVoicePassport(false);
    }
  }, [id]);

  // Watch for transcription completion and auto-reload
  useEffect(() => {
    if (transcriptionStatus.status === 'completed' && transcriptionStatus.transcriptId) {
      console.log('[SessionDetails] Transcription completed, reloading data...');
      // Reload transcripts, speaker roles, and check for assessments
      loadTranscripts();
      loadSpeakerRoles();
      loadAssessments();

      // Refresh assessment card to show new transcripts
      setTranscriptRefreshTrigger(prev => prev + 1);

      // AUTO-TRIGGER: Generate vector embeddings for evidence validation
      triggerTranscriptIngestion(transcriptionStatus.transcriptId);
    }
  }, [transcriptionStatus.status, transcriptionStatus.transcriptId, loadTranscripts, loadAssessments]);

  // Restore instructor assessment generation state from localStorage on mount
  // This handles the case where user navigates away during generation
  useEffect(() => {
    if (!id) return;

    const savedState = localStorage.getItem(INSTRUCTOR_GEN_KEY);
    if (savedState) {
      try {
        const { startedAt } = JSON.parse(savedState);
        const elapsed = Date.now() - startedAt;
        const maxDuration = 10 * 60 * 1000; // 10 minutes max

        if (elapsed < maxDuration) {
          console.log('[SessionDetails] Restoring instructor assessment generation state');
          setGeneratingInstructorAssessment(true);
          // Start progress simulation from where it might be
          const estimatedProgress = Math.min(90, Math.floor(elapsed / 2000) * 10);
          setInstructorAssessmentProgress(estimatedProgress);

          // Poll for completion
          const pollInterval = setInterval(async () => {
            const { data: instructorData } = await supabase
              .from('assessments_instructor')
              .select('id')
              .eq('session_id', id)
              .limit(1);

            if (instructorData && instructorData.length > 0) {
              console.log('[SessionDetails] Instructor assessment detected - generation complete');
              clearInterval(pollInterval);
              localStorage.removeItem(INSTRUCTOR_GEN_KEY);
              setGeneratingInstructorAssessment(false);
              setInstructorAssessmentProgress(0);
              loadAssessments();
              setJobTimelineRefreshKey(prev => prev + 1);
            }
          }, 3000);

          // Clear polling after max duration
          setTimeout(() => {
            clearInterval(pollInterval);
            localStorage.removeItem(INSTRUCTOR_GEN_KEY);
            setGeneratingInstructorAssessment(false);
            setInstructorAssessmentProgress(0);
          }, maxDuration - elapsed);

          return () => clearInterval(pollInterval);
        } else {
          // Expired - clear it
          localStorage.removeItem(INSTRUCTOR_GEN_KEY);
        }
      } catch (e) {
        localStorage.removeItem(INSTRUCTOR_GEN_KEY);
      }
    }
  }, [id, INSTRUCTOR_GEN_KEY, loadAssessments]);

  // Track in-flight ingestion calls to prevent duplicate concurrent requests
  const ingestionInFlight = useRef<Set<string>>(new Set());

  // Automatic transcript ingestion for vector embeddings
  // ENHANCED: Triggers for ALL transcripts in the session, not just the most recent
  const triggerTranscriptIngestion = async (transcriptId: string) => {
    // Prevent duplicate concurrent calls for the same transcript
    if (ingestionInFlight.current.has(transcriptId)) {
      console.log('[SessionDetails] Ingestion already in-flight for transcript:', transcriptId);
      return;
    }
    ingestionInFlight.current.add(transcriptId);
    try {
      console.log('[SessionDetails] Auto-triggering vector embedding generation for transcript:', transcriptId);

      const { data, error } = await supabase.functions.invoke('ingest-transcript', {
        body: {
          session_id: id,
          transcript_id: transcriptId,
        },
      });

      if (error) {
        console.error('[SessionDetails] Transcript ingestion failed:', error);
        console.error('[SessionDetails] Error details:', JSON.stringify(error, null, 2));
        console.error('[SessionDetails] Response data:', data);
        // Don't show error to user - this is a background operation
        // Evidence validation will fall back to keyword matching
      } else {
        console.log('[SessionDetails] Vector embeddings generated successfully for enhanced evidence validation');
        console.log('[SessionDetails] Response:', data);
      }
    } catch (error) {
      console.error('[SessionDetails] Error triggering transcript ingestion:', error);
      // Silent fail - not critical for user experience
    } finally {
      ingestionInFlight.current.delete(transcriptId);
    }
  };

  // Trigger embeddings for ALL transcripts when component loads
  useEffect(() => {
    const triggerAllTranscriptEmbeddings = async () => {
      if (transcripts.length === 0) return;

      console.log(`[SessionDetails] Checking embeddings for ${transcripts.length} transcript(s)...`);

      for (const transcript of transcripts) {
        // Skip transcripts that don't have text yet (still being processed by Inngest pipeline)
        if (!transcript.word_count || transcript.word_count === 0) {
          console.log(`[SessionDetails] Skipping transcript ${transcript.id} - no text yet (word_count: ${transcript.word_count})`);
          continue;
        }

        // Check if embeddings already exist
        const { data: chunks } = await supabase
          .from('transcript_chunks')
          .select('id')
          .eq('transcript_id', transcript.id)
          .limit(1);

        if (!chunks || chunks.length === 0) {
          console.log(`[SessionDetails] Triggering embeddings for transcript ${transcript.id}...`);
          await triggerTranscriptIngestion(transcript.id);
          // Small delay between triggers to avoid overwhelming the system
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          console.log(`[SessionDetails] Embeddings already exist for transcript ${transcript.id}`);
        }
      }
    };

    triggerAllTranscriptEmbeddings();
  }, [transcripts]);

  // Open transcript viewer
  const openTranscriptViewer = (transcriptId?: string) => {
    const idToUse = transcriptId || selectedTranscriptId;
    if (!idToUse) return;
    setShowTranscriptViewer(true);
  };

  // Handle transcript selection
  const handleTranscriptSelect = (transcriptId: string) => {
    setSelectedTranscriptId(transcriptId);
  };

  // Load files for this session
  const loadFiles = useCallback(async () => {
    if (!id) return;

    setFilesLoading(true);
    try {
      const { data, error } = await supabase
        .from('files')
        .select('id, file_name, file_type, file_size, storage_path, created_at')
        .eq('session_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setFiles(data || []);
    } catch (err) {
      console.error('Error loading files:', err);
    } finally {
      setFilesLoading(false);
    }
  }, [id]);

  // Load session recordings (in-app recorded audio)
  const loadRecordings = useCallback(async () => {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from('session_recordings')
        .select('id, session_id, phase, storage_path, duration_seconds, file_size_bytes, status, created_at')
        .eq('session_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRecordings(data || []);
    } catch (err) {
      console.error('Error loading recordings:', err);
    }
  }, [id]);

  // Load session data - extracted as useCallback so it can be reused
  const loadSession = useCallback(async () => {
    if (!id) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          id,
          organization_id,
          session_date,
          aircraft_type,
          flight_phase,
          scenario,
          instructor_notes,
          status,
          created_at,
          trainee_id,
          instructor_id,
          cockpit_language,
          trainee:trainee_id(full_name, email),
          instructor:instructor_id(full_name),
          airline:organization_id(language_code)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Validate and transform data - handle array or object responses
      const traineeData = Array.isArray(data.trainee)
        ? (data.trainee.length > 0 ? data.trainee[0] : null)
        : data.trainee;
      const instructorData = Array.isArray(data.instructor)
        ? (data.instructor.length > 0 ? data.instructor[0] : null)
        : data.instructor;
      const airlineData = Array.isArray(data.airline)
        ? (data.airline.length > 0 ? data.airline[0] : null)
        : data.airline;

      if (!instructorData) {
        throw new Error('Session data is incomplete');
      }

      const transformedSession: SessionData = {
        ...data,
        trainee: traineeData ?? null,
        instructor: instructorData,
        airline: airlineData ?? undefined,
      };

      setSession(transformedSession);

      // Fetch crew with org_pilot fallback for non-registered pilots
      const { data: crewData, error: crewError } = await supabase
        .from('session_crew')
        .select('user_id, org_pilot_id, crew_role, users:user_id(full_name), org_pilots:org_pilot_id(full_name, airline_code)')
        .eq('session_id', id);

      if (crewError) {
        console.warn('[SessionDetails] Unable to load session crew:', crewError.message);
      } else {
        type CrewRow = {
          user_id: string | null;
          org_pilot_id: string | null;
          crew_role: 'captain' | 'first_officer';
          users: { full_name: string } | null;
          org_pilots: { full_name: string; airline_code: string | null } | null;
        };
        const transformedCrew = ((crewData ?? []) as unknown as CrewRow[]).map((member) => ({
          user_id: member.user_id || member.org_pilot_id || '',
          crew_role: member.crew_role,
          full_name: member.users?.full_name || member.org_pilots?.full_name || 'Unknown',
        }));
        setPilotCrew(transformedCrew);
      }

      // Load files, recordings, sync recording transcripts, then load transcripts and remaining data
      await loadFiles();
      await loadRecordings();

      await loadTranscripts();
      await loadSpeakerRoles();
      await loadAssessments();
      await loadDebriefingGuide();
    } catch (err) {
      console.error('Error loading session:', err);
      setError('Failed to load session');
    } finally {
      setLoading(false);
    }
  }, [id, loadFiles, loadRecordings, loadTranscripts, loadSpeakerRoles, loadAssessments, loadDebriefingGuide]);

  // Load session on mount - WAIT for user auth to be established first
  // This prevents race conditions where queries run before RLS auth is ready
  useEffect(() => {
    if (!user) {
      console.log('[SessionDetails] Waiting for auth before loading session...');
      return;
    }
    console.log('[SessionDetails] Auth ready, loading session for user:', user.id);
    loadSession();
  }, [loadSession, user]);

  // Initialize cockpit language from session → airline → browser locale (priority order)
  // When auto-detected from airline or browser locale, persist to DB so the
  // transcription pipeline reads the correct language (it reads from DB, not local state).
  useEffect(() => {
    if (!session || secondLanguageInitialized) return;
    const supported = COCKPIT_LANGUAGE_OPTIONS.filter(o => o.code !== null).map(o => o.code as string);

    // Priority 1: Persisted session-level cockpit_language (already in DB, no save needed)
    if (session.cockpit_language && supported.includes(session.cockpit_language)) {
      setSecondLanguage(session.cockpit_language);
      setSecondLanguageInitialized(true);
      return;
    }

    // Priority 2: Airline-level language_code default
    const airlineLang = session.airline?.language_code;
    if (airlineLang && airlineLang !== 'en' && supported.includes(airlineLang)) {
      setSecondLanguage(airlineLang);
      setSecondLanguageInitialized(true);
      // Persist auto-detected language to DB
      if (id) {
        supabase.from('sessions').update({ cockpit_language: airlineLang }).eq('id', id)
          .then(({ error: e }) => { if (e) console.error('[SessionDetails] Failed to persist cockpit_language:', e); });
      }
      return;
    }

    // Priority 3: Browser locale
    const browserLang = navigator.language?.split('-')[0]?.toLowerCase();
    if (browserLang && browserLang !== 'en' && supported.includes(browserLang)) {
      setSecondLanguage(browserLang);
      setSecondLanguageInitialized(true);
      // Persist auto-detected language to DB
      if (id) {
        supabase.from('sessions').update({ cockpit_language: browserLang }).eq('id', id)
          .then(({ error: e }) => { if (e) console.error('[SessionDetails] Failed to persist cockpit_language:', e); });
      }
      return;
    }

    // Default: null (English only)
    setSecondLanguageInitialized(true);
  }, [session, secondLanguageInitialized, id]);

  // Clean up audio playback on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Handle file upload completion
  const handleUploadComplete = async (
    storagePath: string, 
    originalFileName: string,
    fileSize?: number,
    mimeType?: string
  ) => {
    if (!id || !session || !user) return;

    try {
      console.log('[FileUpload] Processing file metadata:', {
        path: storagePath,
        name: originalFileName,
        size: fileSize,
        mime: mimeType
      });

      // Determine file type from extension
      const ext = originalFileName.split('.').pop()?.toLowerCase();
      let fileType: 'audio' | 'text' | 'document' | 'transcript' = 'document';
      
      if (['mp3', 'wav', 'm4a'].includes(ext || '')) {
        fileType = 'audio';
      } else if (['txt', 'rtf'].includes(ext || '')) {
        fileType = 'text';
      } else if (['pdf', 'docx'].includes(ext || '')) {
        fileType = 'document';
      }

      // Insert file metadata into database
      // Use file metadata passed from upload (no need to query storage)
      const { data: fileData, error } = await supabase
        .from('files')
        .insert({
          organization_id: session.organization_id,
          session_id: id,
          uploaded_by: user.id,
          file_name: originalFileName,
          file_type: fileType,
          mime_type: mimeType || 'application/octet-stream',
          file_size: fileSize || 0,
          storage_path: storagePath,
        })
        .select()
        .single();

      if (error) throw error;

      console.log('[FileUpload] ✅ File metadata saved to database:', fileData);

      // ✅ AUTO-PROCESS TEXT FILES: If this is a text file, automatically run through full pipeline
      if (fileType === 'text') {
        console.log('[SessionDetails] Text file detected - auto-processing through full pipeline...');
        
        try {
          // Download the file content from storage
          const { data: fileBlob, error: downloadError } = await supabase.storage
            .from('session-files')
            .download(storagePath);
          
          if (downloadError) throw downloadError;
          
          // Convert to File object for text extraction
          const file = new File([fileBlob], originalFileName, { type: 'text/plain' });
          
          // Extract text
          const extractedText = await extractTextFromFile(file);
          
          if (!extractedText || extractedText.length < 50) {
            throw new Error('Extracted text is too short (minimum 50 characters required)');
          }
          
          console.log(`[SessionDetails] ✅ Extracted ${extractedText.length} characters from ${originalFileName}`);
          
          // Call new API endpoint for full pipeline processing (parse → anonymize → events → complete)
          console.log('[SessionDetails] 🚀 Starting full pipeline processing...');
          const apiResponse = await fetch('/api/process-text-transcript', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              sessionId: id,
              fileId: fileData.id,
              textContent: extractedText,
              fileName: originalFileName,
            }),
          });
          
          if (!apiResponse.ok) {
            const errorData = await apiResponse.json();
            throw new Error(errorData.details || errorData.error || 'API request failed');
          }
          
          const result = await apiResponse.json();
          
          console.log('[SessionDetails] ✅ Auto-processing complete:', result);
          console.log(`[SessionDetails]   - Transcript ID: ${result.transcriptId}`);
          console.log(`[SessionDetails]   - Segments: ${result.segments}`);
          console.log(`[SessionDetails]   - Events: ${result.events}`);
          console.log(`[SessionDetails]   - Format: ${result.parseFormat}`);

          // Wait for transcript to be fully committed to database before refreshing UI
          const transcriptVerified = await waitForTranscript(result.transcriptId);

          if (transcriptVerified) {
            // Reload transcripts to show assessment button
            await loadTranscripts();

            // Trigger TranscriptList refresh (real-time subscription will also handle this)
            setTranscriptRefreshTrigger(prev => prev + 1);
          } else {
            // The real-time subscription should eventually update the list
            console.warn('[SessionDetails] Could not verify transcript creation. UI will update via real-time subscription.');
          }

        } catch (textError) {
          console.error('[SessionDetails] Failed to auto-process text file:', textError);
          // Don't block the file upload, just log the error
          setError(`Text file uploaded but processing failed: ${(textError as Error).message}. You can manually process it using the "Process" button.`);
        }
      }

      // Reload files and refresh transcript list (for assessment card)
      await loadFiles();
      setTranscriptRefreshTrigger(prev => prev + 1);

      // Close upload section
      setShowUpload(false);

      // Propose combine or stitch when a new audio file is uploaded with existing transcripts
      if (fileType === 'audio') {
        const hasExistingTranscripts = transcripts.some(t => t.processing_status === 'completed' && !t.is_archived);
        // Count audio files including the one just uploaded
        const audioFileCount = files.filter(f => f.file_type === 'audio' || isAudioFile(f.file_name)).length + 1;

        if (hasExistingTranscripts && audioFileCount >= 2) {
          // 2+ audio files with existing transcript → offer stitch (fast) + combine (full re-transcription)
          console.log('[SessionDetails] New audio uploaded with existing transcript — proposing stitch + combine options');
          setLatestAudioFile(fileData);
          setShowDebriefStitchProposal(true);
          setShowCombineProposal(false);
        }
      }

      console.log('[SessionDetails] File uploaded and linked successfully:', fileData.file_name);
    } catch (err) {
      console.error('Error saving file metadata:', err);
      setError(`Failed to link file to session: ${(err as Error).message}`);
    }
  };

  // Download file
  const handleDownload = async (file: FileData) => {
    try {
      const { data, error } = await supabase.storage
        .from('session-files')
        .download(file.storage_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file');
    }
  };

  // Process existing text file into transcript - v2 with full pipeline
  const handleProcessTextFile = async (file: FileData) => {
    if (!id || !session) return;
    
    console.log(`[SessionDetails] Processing text file: ${file.file_name}`);
    setUploadingTranscript(true);
    setTranscriptUploadError(null);
    
    try {
      // 1. Download the file content from storage
      console.log('[SessionDetails] Downloading file from storage...');
      const { data: fileBlob, error: downloadError } = await supabase.storage
        .from('session-files')
        .download(file.storage_path);
      
      if (downloadError) throw downloadError;
      
      // 2. Convert to File object for text extraction
      const fileObj = new File([fileBlob], file.file_name, { type: 'text/plain' });
      
      // 3. Extract text
      console.log('[SessionDetails] Extracting text...');
      const extractedText = await extractTextFromFile(fileObj);
      
      if (!extractedText || extractedText.length < 50) {
        throw new Error('Extracted text is too short (minimum 50 characters required)');
      }
      
      console.log(`[SessionDetails] ✅ Extracted ${extractedText.length} characters from ${file.file_name}`);
      
      // 4. Call new API endpoint for full pipeline processing
      console.log('[SessionDetails] 🚀 Starting full pipeline processing...');
      const apiResponse = await fetch('/api/process-text-transcript', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: id,
          fileId: file.id,
          textContent: extractedText,
          fileName: file.file_name,
        }),
      });
      
      if (!apiResponse.ok) {
        const errorData = await apiResponse.json();
        throw new Error(errorData.details || errorData.error || 'API request failed');
      }
      
      const result = await apiResponse.json();
      
      console.log('[SessionDetails] ✅ Processing complete:', result);
      console.log(`[SessionDetails]   - Transcript ID: ${result.transcriptId}`);
      console.log(`[SessionDetails]   - Word count: ${result.wordCount}`);
      console.log(`[SessionDetails]   - Segments: ${result.segments}`);
      console.log(`[SessionDetails]   - Events detected: ${result.events}`);
      console.log(`[SessionDetails]   - Format: ${result.parseFormat}`);
      console.log(`[SessionDetails]   - Processing time: ${result.processingTime}s`);
      
      // 5. Reload all relevant data
      await Promise.all([
        loadTranscripts(),
        loadFiles(),
        loadSession(), // Refresh session status
      ]);
      
      // 6. Trigger TranscriptList refresh
      setTranscriptRefreshTrigger(prev => prev + 1);
      
      // 7. Show detailed success message
      const successMessage = `✅ Transcript processed successfully!\n\n` +
        `File: ${file.file_name}\n` +
        `Words: ${result.wordCount.toLocaleString()}\n` +
        `Segments: ${result.segments}\n` +
        `Events detected: ${result.events}\n` +
        `Format: ${result.parseFormat}\n` +
        `Time: ${result.processingTime}s\n\n` +
        `Session status updated to 'completed'. You can now generate assessments!`;
      
      alert(successMessage);
      
    } catch (error) {
      console.error('[SessionDetails] ❌ Failed to process text file:', error);
      const errorMessage = `Failed to process text file: ${(error as Error).message}`;
      setTranscriptUploadError(errorMessage);
      setError(errorMessage);
    } finally {
      setUploadingTranscript(false);
    }
  };

  // Delete file
  const handleDeleteFile = async (file: FileData) => {
    if (!confirm(`Are you sure you want to delete "${file.file_name}"?`)) return;

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('session-files')
        .remove([file.storage_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('files')
        .delete()
        .eq('id', file.id);

      if (dbError) throw dbError;

      // Remove from local state
      setFiles((prev) => prev.filter((f) => f.id !== file.id));

      console.log('[SessionDetails] File deleted successfully:', file.file_name);
    } catch (error) {
      console.error('Error deleting file:', error);
      setError('Failed to delete file');
    }
  };

  // Transcribe audio file
  const handleTranscribe = async (file: FileData) => {
    try {
      console.log('[SessionDetails] Starting transcription for file:', file.file_name);

      // Start transcription (this sets transcriptionStatus immediately)
      await startTranscription(session!.id, file.storage_path, file.id, {});

      // Update session status locally to show processing state immediately
      if (session) {
        setSession({
          ...session,
          status: 'transcribing'
        });
      }

      // Reload transcripts to sync with database
      setTimeout(() => {
        loadTranscripts();
        // Trigger TranscriptList refresh
        setTranscriptRefreshTrigger(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting transcription:', error);
      setError('Failed to start transcription. Please try again.');
    }
  };

  // Get all audio files from the files list
  const getAudioFiles = useCallback((): AudioFile[] => {
    return files
      .filter(f => f.file_type === 'audio' || isAudioFile(f.file_name))
      .map(f => ({
        id: f.id,
        file_name: f.file_name,
        storage_path: f.storage_path,
        file_size: f.file_size,
      }));
  }, [files]);

  // Get the ordered audio files (custom order or auto-sorted)
  // Uses ALL audio files so that already-transcribed files are included in combine
  const getOrderedAudioFiles = useCallback((): AudioFile[] => {
    const audioFiles = getAudioFiles();

    // Use custom order if set and still valid (same files)
    if (customFileOrder && customFileOrder.length === audioFiles.length) {
      const customIds = new Set(customFileOrder.map(f => f.id));
      const audioIds = new Set(audioFiles.map(f => f.id));
      const sameFiles = audioFiles.every(f => customIds.has(f.id)) &&
                        customFileOrder.every(f => audioIds.has(f.id));
      if (sameFiles) {
        return customFileOrder;
      }
    }

    // Fall back to auto-sorted order
    return sortAudioFiles(audioFiles);
  }, [getAudioFiles, customFileOrder]);

  // Move a file up in the concatenation order
  const moveFileUp = (index: number) => {
    const ordered = getOrderedAudioFiles();
    if (index <= 0 || index >= ordered.length) return;

    const newOrder = [...ordered];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index]!, newOrder[index - 1]!];
    setCustomFileOrder(newOrder);
  };

  // Move a file down in the concatenation order
  const moveFileDown = (index: number) => {
    const ordered = getOrderedAudioFiles();
    if (index < 0 || index >= ordered.length - 1) return;

    const newOrder = [...ordered];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1]!, newOrder[index]!];
    setCustomFileOrder(newOrder);
  };

  // Reset to automatic sorting
  const resetFileOrder = () => {
    setCustomFileOrder(null);
  };

  // Transcribe all audio files (concatenate first if multiple)
  const handleTranscribeAll = async () => {
    if (!session) return;

    const audioFiles = getAudioFiles();
    const orderedFiles = getOrderedAudioFiles();

    if (audioFiles.length === 0) {
      setError('No audio files found to transcribe.');
      return;
    }

    // Single file: use normal transcription
    if (audioFiles.length === 1) {
      const singleAudioFile = audioFiles[0]!;
      const file = files.find(f => f.id === singleAudioFile.id);
      if (file) {
        await handleTranscribe(file);
      }
      return;
    }

    // Multiple files: concatenate server-side first, then transcribe
    console.log(`[SessionDetails] Concatenating ${orderedFiles.length} audio files SERVER-SIDE before transcription`);
    console.log(`[SessionDetails] Order: ${orderedFiles.map(f => f.file_name).join(' → ')}`);

    try {
      // Concatenate audio files server-side (much faster - same datacenter)
      // Server handles deletion of source files automatically
      const result = await concatenateAudioServerSide(
        orderedFiles,
        session.id,
        session.organization_id,
        true, // deleteSourceFiles
        (progress) => {
          setConcatenationProgress(progress);
          console.log(`[SessionDetails] Concatenation progress: ${progress.stage} - ${progress.progress}%`);
        }
      );

      console.log(`[SessionDetails] Server-side concatenation complete. Combined file: ${result.storagePath}`);
      console.log(`[SessionDetails] Source files: ${result.sourceFiles.join(' + ')}`);
      console.log(`[SessionDetails] Duration: ${result.duration.toFixed(1)}s`);

      // Reload files to show the new combined file (source files already deleted by server)
      await loadFiles();

      // Clear concatenation progress
      setConcatenationProgress(null);

      // Start transcription on the combined file
      console.log('[SessionDetails] Starting transcription on combined audio file');
      await startTranscription(session.id, result.storagePath, result.fileId, {});

      // Update session status locally
      setSession({
        ...session,
        status: 'transcribing'
      });

      // Reload transcripts after a delay
      setTimeout(() => {
        loadTranscripts();
        setTranscriptRefreshTrigger(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('[SessionDetails] Failed to concatenate/transcribe audio files:', error);
      setConcatenationProgress(null);
      setError(`Failed to process audio files: ${(error as Error).message}`);
    }
  };

  // Combine audio files only (without transcription) - server-side
  const handleCombineOnly = async () => {
    if (!session) return;

    const orderedFiles = getOrderedAudioFiles();

    if (orderedFiles.length < 2) {
      setError('Need at least 2 audio files to combine.');
      return;
    }

    console.log(`[SessionDetails] Combining ${orderedFiles.length} audio files SERVER-SIDE (without transcription)`);
    console.log(`[SessionDetails] Order: ${orderedFiles.map(f => f.file_name).join(' → ')}`);

    try {
      // Concatenate audio files server-side (much faster - same datacenter)
      // Server handles deletion of source files automatically
      const result = await concatenateAudioServerSide(
        orderedFiles,
        session.id,
        session.organization_id,
        true, // deleteSourceFiles
        (progress) => {
          setConcatenationProgress(progress);
          console.log(`[SessionDetails] Concatenation progress: ${progress.stage} - ${progress.progress}%`);
        }
      );

      console.log(`[SessionDetails] Server-side concatenation complete. Combined file: ${result.storagePath}`);
      console.log(`[SessionDetails] Source files: ${result.sourceFiles.join(' + ')}`);
      console.log(`[SessionDetails] Duration: ${result.duration.toFixed(1)}s`);

      // Reload files to show the new combined file (source files already deleted by server)
      await loadFiles();

      // Clear concatenation progress
      setConcatenationProgress(null);

      // Don't start transcription - user can click Transcribe button on the combined file

    } catch (error) {
      console.error('[SessionDetails] Failed to combine audio files:', error);
      setConcatenationProgress(null);
      setError(`Failed to combine audio files: ${(error as Error).message}`);
    }
  };

  // Combine ALL audio files and re-transcribe (used when debriefing audio is added)
  // Archives existing transcripts so assessments only use the new combined transcript
  const handleCombineAndRetranscribe = async () => {
    if (!session) return;

    // Use ALL audio files (not just untranscribed ones)
    const allAudioFiles = getAudioFiles();
    const orderedFiles = sortAudioFiles(allAudioFiles);

    if (orderedFiles.length < 2) {
      setError('Need at least 2 audio files to combine.');
      return;
    }

    try {
      setShowCombineProposal(false);

      // Step 1: Concatenate ALL audio files server-side
      console.log(`[SessionDetails] Combining ALL ${orderedFiles.length} audio files for re-transcription`);
      console.log(`[SessionDetails] Order: ${orderedFiles.map(f => f.file_name).join(' → ')}`);

      const result = await concatenateAudioServerSide(
        orderedFiles,
        session.id,
        session.organization_id,
        true, // deleteSourceFiles (individual audio files)
        (progress) => {
          setConcatenationProgress(progress);
          console.log(`[SessionDetails] Concatenation progress: ${progress.stage} - ${progress.progress}%`);
        }
      );

      console.log(`[SessionDetails] Server-side concatenation complete. Combined file: ${result.storagePath}`);

      // Reload files to show the new combined file
      await loadFiles();
      setConcatenationProgress(null);

      // Step 2: Archive ALL existing transcripts for this session
      // Query database directly instead of relying on potentially stale React state
      const { data: existingTranscripts, error: fetchError } = await supabase
        .from('transcripts')
        .select('id')
        .eq('session_id', session.id)
        .or('is_archived.is.null,is_archived.eq.false');

      if (fetchError) {
        console.warn('[SessionDetails] Failed to fetch existing transcripts for archival:', fetchError);
      }

      const transcriptIds = (existingTranscripts || []).map(t => t.id);
      if (transcriptIds.length > 0) {
        console.log(`[SessionDetails] Archiving ${transcriptIds.length} existing transcript(s) before re-transcription`);
        const { error: archiveError } = await supabase
          .from('transcripts')
          .update({ is_archived: true })
          .in('id', transcriptIds);

        if (archiveError) {
          console.error('[SessionDetails] Failed to archive transcripts:', archiveError);
          // Non-fatal: continue with transcription anyway
        } else {
          console.log(`[SessionDetails] Archived ${transcriptIds.length} transcript(s)`);
        }
      }

      // Step 3: Clear stale non-manual speaker role mappings (P1 fix B-02)
      // Re-diarization may flip/rename speaker labels, making old mappings wrong.
      // Delete auto-detected mappings so user is prompted to re-assign after re-diarization.
      const { error: clearMappingsError } = await supabase
        .from('speaker_role_mappings')
        .delete()
        .eq('session_id', session.id)
        .eq('manual_override', false);

      if (clearMappingsError) {
        console.warn('[SessionDetails] Failed to clear stale role mappings:', clearMappingsError);
      } else {
        console.log('[SessionDetails] Cleared stale auto role mappings. User will re-confirm after transcription.');
      }

      // Step 4: Start transcription on combined file
      console.log('[SessionDetails] Starting transcription on combined audio file');
      await startTranscription(session.id, result.storagePath, result.fileId, {});

      // Update session status locally
      setSession({
        ...session,
        status: 'transcribing'
      });

      // Reload transcripts after a delay
      setTimeout(() => {
        loadTranscripts();
        setTranscriptRefreshTrigger(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('[SessionDetails] Failed to combine/retranscribe:', error);
      setConcatenationProgress(null);
      setError(`Failed to combine and retranscribe: ${(error as Error).message}`);
    }
  };

  // Debriefing stitch: transcribe just the new debriefing audio and stitch with existing transcript
  const handleDebriefTranscribe = async () => {
    if (!session || !latestAudioFile) return;

    try {
      setShowDebriefStitchProposal(false);
      console.log(`[SessionDetails] 🧵 Debriefing stitch: transcribing ${latestAudioFile.file_name} and stitching with existing transcript`);

      // Start transcription of just this file, with stitch flag
      await startTranscription(
        session.id,
        latestAudioFile.storage_path,
        latestAudioFile.id,
        { triggerStitch: true, phase: 'debriefing' }
      );

      // Update session status locally
      setSession({
        ...session,
        status: 'transcribing'
      });

      // Reload transcripts after a delay
      setTimeout(() => {
        loadTranscripts();
        setTranscriptRefreshTrigger(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('[SessionDetails] Failed to start debriefing transcription:', error);
      setError(`Failed to transcribe debriefing: ${(error as Error).message}`);
    }
  };

  // MAD-17: Handle manual transcript upload
  const handleManualTranscriptUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !id || !session || !user) return;

    setUploadingTranscript(true);
    setTranscriptUploadError(null);

    try {
      console.log('[ManualTranscript] Uploading file:', file.name);

      // Step 1: Upload file to storage (so it appears in files list)
      const timestamp = Date.now();
      const storagePath = `${session.organization_id}/${id}/${timestamp}-${file.name}`;
      
      const { error: uploadError } = await supabase.storage
        .from('session-files')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        throw new Error(`File upload failed: ${uploadError.message}`);
      }

      console.log('[ManualTranscript] File uploaded to storage:', storagePath);

      // Step 2: Extract text from file
      const extractedText = await extractTextFromFile(file);
      
      if (!extractedText || extractedText.length < 10) {
        throw new Error('Extracted text is too short or empty');
      }

      console.log(`[ManualTranscript] Extracted ${extractedText.length} characters`);

      // Step 3: Calculate metadata
      const wordCount = extractedText.split(/\s+/).filter(word => word.length > 0).length;
      const estimatedDuration = Math.round(wordCount / 150 * 60); // Assume 150 words/minute

      console.log(`[ManualTranscript] Word count: ${wordCount}, Estimated duration: ${estimatedDuration}s`);

      // Step 4: Save file metadata to files table
      const { data: fileData, error: fileError } = await supabase
        .from('files')
        .insert({
          organization_id: session.organization_id,
          session_id: id,
          uploaded_by: user.id,
          file_name: file.name,
          file_type: 'text',
          mime_type: file.type || 'text/plain',
          file_size: file.size,
          storage_path: storagePath,
        })
        .select()
        .single();

      if (fileError) {
        throw new Error(`Failed to save file metadata: ${fileError.message}`);
      }

      console.log('[ManualTranscript] File metadata saved:', fileData.id);

      // Step 5: Create transcript record with status 'pending'
      // User will click "Process" button to run full pipeline (segments + events)
      const { data: transcriptData, error: transcriptError } = await supabase
        .from('transcripts')
        .insert({
          session_id: id,
          file_id: fileData.id, // 🔥 NEW: Link to file record
          original_text: extractedText,
          anonymized_text: extractedText, // Use original text temporarily (will be updated during processing)
          word_count: wordCount,
          duration_seconds: estimatedDuration,
          processing_status: 'pending', // 🔥 NEW: Mark as pending, not completed
          organization_id: session.organization_id,
        })
        .select()
        .single();

      if (transcriptError) {
        console.error('[ManualTranscript] Database error:', transcriptError);
        throw new Error(`Database error: ${transcriptError.message || JSON.stringify(transcriptError)}`);
      }

      console.log('[ManualTranscript] Transcript created (pending processing):', transcriptData.id);

      // Step 6: Wait for transcript to be fully committed to database
      const transcriptVerified = await waitForTranscript(transcriptData.id);

      if (transcriptVerified) {
        // Step 7: Reload data
        await Promise.all([
          loadTranscripts(),
          loadFiles(), // 🔥 NEW: Reload files list so the file appears
        ]);

        // Trigger TranscriptList refresh (real-time subscription will also handle this)
        setTranscriptRefreshTrigger(prev => prev + 1);
      } else {
        // The real-time subscription should eventually update the list
        console.warn('[ManualTranscript] Could not verify transcript creation. UI will update via real-time subscription.');
      }

      // Success message - inform user to click "Process" button
      alert(
        `✅ Transcript uploaded successfully!\n\n` +
        `File: ${file.name}\n` +
        `Words: ${wordCount.toLocaleString()}\n\n` +
        `⚠️ Click the "🚀 Process" button in the transcript list to:\n` +
        `  • Parse speakers and segments\n` +
        `  • Detect events\n` +
        `  • Enable assessment generation`
      );

      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('[ManualTranscript] Upload failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setTranscriptUploadError(`Failed to upload transcript: ${errorMessage}`);
    } finally {
      setUploadingTranscript(false);
    }
  };

  // NEW: Process a pending transcript through the full pipeline
  const handleProcessPendingTranscript = async (transcriptId: string) => {
    if (!id || !session) return;
    
    console.log(`[SessionDetails] Processing pending transcript: ${transcriptId}`);
    setProcessingTranscriptId(transcriptId);
    setError(null);
    
    try {
      // 1. Fetch the transcript data
      const { data: transcript, error: fetchError } = await supabase
        .from('transcripts')
        .select('original_text')
        .eq('id', transcriptId)
        .single();
      
      if (fetchError || !transcript) {
        throw new Error('Transcript not found');
      }
      
      const extractedText = transcript.original_text;
      
      if (!extractedText || extractedText.length < 50) {
        throw new Error('Transcript text is too short (minimum 50 characters required)');
      }
      
      console.log(`[SessionDetails] Processing ${extractedText.length} characters...`);
      
      // 2. Call the full pipeline API
      console.log('[SessionDetails] 🚀 Starting full pipeline processing...');
      const apiResponse = await fetch('/api/process-text-transcript', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: id,
          transcriptId: transcriptId, // Pass existing transcript ID to update it
          textContent: extractedText,
          fileName: 'Manual Transcript',
        }),
      });
      
      if (!apiResponse.ok) {
        const errorData = await apiResponse.json();
        throw new Error(errorData.details || errorData.error || 'API request failed');
      }
      
      const result = await apiResponse.json();
      
      console.log('[SessionDetails] ✅ Processing complete:', result);
      console.log(`[SessionDetails]   - Word count: ${result.wordCount}`);
      console.log(`[SessionDetails]   - Segments: ${result.segments}`);
      console.log(`[SessionDetails]   - Events detected: ${result.events}`);
      console.log(`[SessionDetails]   - Format: ${result.parseFormat}`);
      console.log(`[SessionDetails]   - Processing time: ${result.processingTime}s`);
      
      // 3. Reload all relevant data
      await Promise.all([
        loadTranscripts(),
        loadSession(), // Refresh session status
      ]);
      
      // 4. Trigger TranscriptList refresh
      setTranscriptRefreshTrigger(prev => prev + 1);
      
      // 5. Show success message
      alert(
        `✅ Transcript processed successfully!\n\n` +
        `Words: ${result.wordCount.toLocaleString()}\n` +
        `Segments: ${result.segments}\n` +
        `Events detected: ${result.events}\n` +
        `Format: ${result.parseFormat}\n` +
        `Time: ${result.processingTime}s\n\n` +
        `Session is now ready for assessment generation!`
      );
      
    } catch (error) {
      console.error('[SessionDetails] ❌ Failed to process transcript:', error);
      const errorMessage = `Failed to process transcript: ${(error as Error).message}`;
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setProcessingTranscriptId(null);
    }
  };

  // Combine multiple transcripts into one with adjusted timestamps
  const handleCombineTranscripts = async (transcriptIds: string[]) => {
    if (!session || transcriptIds.length < 2) return;

    console.log(`[SessionDetails] Combining ${transcriptIds.length} transcripts...`);
    setError(null);

    try {
      const response = await fetch('/api/combine-transcripts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({
          sessionId: session.id,
          organizationId: session.organization_id,
          transcriptIds: transcriptIds,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to combine transcripts');
      }

      const result = await response.json();
      console.log('[SessionDetails] Combined transcript created:', result);

      // Reload transcripts to show the new combined transcript
      await loadTranscripts();
      setTranscriptRefreshTrigger(prev => prev + 1);

      // Auto-select the combined transcript
      if (result.combinedTranscript?.id) {
        setSelectedTranscriptId(result.combinedTranscript.id);
      }

      alert(
        `Transcripts combined successfully!\n\n` +
        `Combined from: ${transcriptIds.length} transcripts\n` +
        `Total words: ${result.combinedTranscript?.wordCount?.toLocaleString() || 'N/A'}\n` +
        `Total duration: ${Math.floor((result.combinedTranscript?.totalDuration || 0) / 60)}:${String((result.combinedTranscript?.totalDuration || 0) % 60).padStart(2, '0')}\n\n` +
        `The combined transcript is now ready for assessment generation.`
      );
    } catch (error) {
      console.error('[SessionDetails] Failed to combine transcripts:', error);
      const errorMessage = `Failed to combine transcripts: ${(error as Error).message}`;
      setError(errorMessage);
      alert(errorMessage);
      throw error; // Re-throw so TranscriptList knows it failed
    }
  };

  // Delete all assessments for rapid testing
  const handleDeleteAssessments = async () => {
    if (!session) return;

    // Strong confirmation dialog
    const confirmMessage =
      'Are you sure you want to DELETE ALL ASSESSMENTS?\n\n' +
      'This will permanently remove:\n' +
      '• All pilot competency assessments\n' +
      '• All instructor effectiveness assessments\n' +
      '• All debriefing guides and events\n' +
      '• All evidence validation logs\n' +
      '• All validation jobs and batches\n\n' +
      'Session, transcripts, and files will be preserved.\n\n' +
      'This action CANNOT be undone!';

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      console.log(`[SessionDetails] Deleting all assessments for session ${session.id}...`);

      const result = await deleteAssessments(session.id);

      console.log('[SessionDetails] Assessments deleted successfully:', result);

      // Show success message with deletion counts
      const message =
        `✅ Successfully deleted ${result.deletedCounts.pilotAssessments + result.deletedCounts.instructorAssessments + result.deletedCounts.debriefingGuides} assessments and guides!\n\n` +
        `Pilot Assessments: ${result.deletedCounts.pilotAssessments}\n` +
        `Instructor Assessments: ${result.deletedCounts.instructorAssessments}\n` +
        `Debriefing Guides: ${result.deletedCounts.debriefingGuides}\n` +
        `Debriefing Events: ${result.deletedCounts.debriefingEvents}\n` +
        `Debriefing UAS: ${result.deletedCounts.debriefingUas}\n` +
        `Validation Logs: ${result.deletedCounts.validationLogs}\n` +
        `Validation Jobs: ${result.deletedCounts.validationJobs}\n` +
        `Validation Batches: ${result.deletedCounts.validationBatches}\n\n` +
        `You can now regenerate assessments for rapid testing!`;

      alert(message);

      // Reload the page to reflect the changes
      window.location.reload();

    } catch (error) {
      console.error('[SessionDetails] Failed to delete assessments:', error);
      const errorMessage = `Failed to delete assessments: ${(error as Error).message}`;
      setError(errorMessage);
      alert(errorMessage);
    }
  };

  // Get file icon
  const getFileIcon = (type: string) => {
    switch (type) {
      case 'audio':
        return '🎵';
      case 'text':
      case 'transcript':
        return '📄';
      case 'document':
        return '📎';
      default:
        return '📎';
    }
  };

  // Audio playback: toggle play/pause for uploaded files or recordings
  const handleToggleAudio = async (
    itemId: string,
    bucket: 'session-files' | 'session-recordings',
    storagePath: string
  ) => {
    // If already playing this item, stop it
    if (playingAudioId === itemId) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingAudioId(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(storagePath, 3600); // 1 hour signed URL

      if (error || !data?.signedUrl) {
        console.error('Error creating signed URL:', error);
        // Don't use setError here — that triggers the "Session Not Found" page
        alert('Cannot play this recording. It may be stored as chunks — wait for concatenation to complete.');
        return;
      }

      const audio = new Audio(data.signedUrl);
      audio.onended = () => {
        setPlayingAudioId(null);
        audioRef.current = null;
      };
      audio.onerror = () => {
        console.error('Audio playback error');
        setPlayingAudioId(null);
        audioRef.current = null;
      };

      audioRef.current = audio;
      setPlayingAudioId(itemId);
      await audio.play();
    } catch (err) {
      console.error('Error playing audio:', err);
      setPlayingAudioId(null);
    }
  };

  // Format recording duration (seconds → mm:ss)
  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Recording phase display label
  const phaseLabel = (phase: string): string => {
    switch (phase) {
      case 'briefing': return 'Briefing';
      case 'simulator': return 'Simulator';
      case 'debriefing': return 'Debriefing';
      default: return phase;
    }
  };

  // Recording status badge
  const recordingStatusBadge = (status: string) => {
    switch (status) {
      case 'transcribed':
      case 'completed':
        return <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">Transcribed</span>;
      case 'transcribing':
        return <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Transcribing...</span>;
      case 'uploaded':
        return <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">Uploaded</span>;
      case 'error':
        return <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">Error</span>;
      default:
        return <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate"></div>
          <p className="mt-2 text-steel">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold text-navy mb-2">Session Not Found</h2>
          <p className="text-steel mb-6">{error || 'This session does not exist'}</p>
          <button onClick={() => navigate('/sessions')} className="btn-primary">
            Back to Sessions
          </button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-steel/20 text-slate';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-steel/20 text-slate';
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-white">
      {/* Compact Navigation Bar */}
      <div className="bg-navy px-4 py-2 flex items-center justify-between shrink-0 shadow-sm">
        <button
          onClick={() => navigate('/sessions')}
          className="text-white hover:text-sky-200 text-sm font-medium flex items-center gap-2"
        >
          ← Back to Sessions
        </button>
        <span className="text-white/70 text-sm">
          {pilotCrew.find(m => m.crew_role === 'captain')?.full_name || 'Captain'} • {session.trainee?.full_name || 'First Officer'} • {session.aircraft_type || 'Aircraft'} • {session.session_date ? new Date(session.session_date).toLocaleDateString() : ''}
        </span>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-[1800px] mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-navy mb-2">Training Session Details</h1>
                <p className="text-steel">Session ID: {session.id.slice(0, 8)}...</p>
              </div>
              <span
                className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(
                  session.status
                )}`}
              >
                {session.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>

      {/* Transcription Progress Banner - Always visible when processing */}
      {transcriptionStatus.status === 'processing' && (
        <div className="bg-gradient-to-r from-sky/10 to-blue-50 border-2 border-orca-elevate rounded-lg shadow-lg p-6 mb-6 animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate"></div>
              <div>
                <h3 className="text-lg font-semibold text-navy">🎤 Transcribing Audio...</h3>
                <p className="text-sm text-steel">Processing your session audio with AI</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-orca-elevate">{transcriptionStatus.progress}%</div>
              <div className="text-xs text-steel">Progress</div>
            </div>
          </div>
          <ProgressBar
            progress={transcriptionStatus.progress}
            label=""
            size="lg"
            color="sky"
            animated={true}
            showPercentage={false}
          />
          <div className="mt-3 text-xs text-steel/70">
            {transcriptionStatus.progress < 25 && '📥 Downloading audio file...'}
            {transcriptionStatus.progress >= 25 && transcriptionStatus.progress < 50 && '🗣️ Converting speech to text...'}
            {transcriptionStatus.progress >= 50 && transcriptionStatus.progress < 75 && '👥 Identifying speakers...'}
            {transcriptionStatus.progress >= 75 && '✨ Finalizing transcript...'}
          </div>
        </div>
      )}

      {/* ===== Generate Reports Card ===== */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-steel/10">
        <div className="mb-6 pb-4 border-b border-steel/10">
          <h2 className="text-lg font-semibold text-navy">Generate Reports</h2>
          <p className="text-sm text-steel mt-1">Create assessment outputs and analysis reports</p>
        </div>

        {/* Step 1: Speaker Role Assignments */}
        {/* Only show when there are completed transcripts (hides during first-time transcription) */}
        {hasCompletedTranscripts && (
          <div className="mb-6">
            <div className="rounded-lg p-4 bg-green-50 border border-green-200">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-green-600 flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                  <h3 className="text-base font-bold text-navy">
                    Step 1: Speaker Role Assignments
                  </h3>
                  {speakerRoleAssignments.length > 0 && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                      Assigned
                    </span>
                  )}
                </div>
              </div>

              {loadingSpeakerRoles ? (
                <div className="flex items-center py-3">
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-orca-elevate"></div>
                  <span className="ml-2 text-steel text-sm">Loading speaker roles...</span>
                </div>
              ) : speakerRoleAssignments.length > 0 ? (
                <div className="mt-2">
                  <p className="text-sm text-green-700 mb-2.5 flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Speaker roles have been assigned. Pilot assessments will only extract evidence from pilot speech.
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-2.5">
                    {speakerRoleAssignments.map((assignment) => (
                      <div
                        key={assignment.speaker_label}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs ${
                          assignment.speaker_role === 'instructor'
                            ? 'bg-blue-50 border-blue-200 text-blue-800'
                            : assignment.speaker_role === 'captain' || assignment.speaker_role === 'captain_2'
                            ? 'bg-purple-50 border-purple-200 text-purple-800'
                            : assignment.speaker_role === 'first_officer' || assignment.speaker_role === 'first_officer_2'
                            ? 'bg-green-50 border-green-200 text-green-800'
                            : assignment.speaker_role === 'observer'
                            ? 'bg-slate-50 border-slate-200 text-slate-700'
                            : assignment.speaker_role === 'other'
                            ? 'bg-gray-50 border-gray-200 text-gray-700'
                            : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                        }`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {assignment.speaker_label}: {
                          assignment.speaker_role === 'instructor' ? 'Instructor'
                          : assignment.speaker_role === 'captain' ? 'Captain'
                          : assignment.speaker_role === 'captain_2' ? 'Captain 2'
                          : assignment.speaker_role === 'first_officer' ? 'First Officer'
                          : assignment.speaker_role === 'first_officer_2' ? 'First Officer 2'
                          : assignment.speaker_role === 'observer' ? 'Observer'
                          : assignment.speaker_role === 'other' ? 'Other'
                          : 'Unknown'
                        }
                      </div>
                    ))}
                  </div>
                  <div className="ml-10">
                    <button
                      onClick={() => setShowSpeakerRoleModal(true)}
                      className="px-3 py-1 text-xs text-orca-elevate border border-orca-elevate rounded-md bg-white hover:bg-orca-elevate/5 transition-colors"
                    >
                      Re-assign Speaker Roles
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ml-10 mt-3">
                  {hasVoicePassport ? (
                    <>
                      <p className="text-sm text-sky-700 mb-3 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-sky-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        Speaker profiles from your briefing will be applied automatically. No need to re-assign roles.
                      </p>
                      <button
                        onClick={() => setShowSpeakerRoleModal(true)}
                        className="px-3 py-1 text-xs text-steel border border-steel/30 rounded-md bg-white hover:bg-gray-50 transition-colors"
                      >
                        Review Speaker Roles
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-amber-700 mb-3 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Speaker roles have not been assigned. For accurate pilot assessments, please assign speaker roles.
                      </p>
                      <button
                        onClick={() => setShowSpeakerRoleModal(true)}
                        className="px-4 py-2 text-sm font-medium border-2 border-orca-elevate bg-white text-orca-elevate rounded-md hover:bg-orca-elevate/5 transition-colors"
                      >
                        Assign Speaker Roles
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Generate Debriefing Guide Button - Step 2 (shown when completed transcripts exist but no guide yet) */}
        {hasCompletedTranscripts && !debriefingGuideStatus.exists && (
          <div className="mb-6">
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-navy leading-tight">
                    Step 2: Generate Debriefing Preparation Report
                  </h3>
                  <p className="text-steel text-xs">
                    Analyzes all transcripts to prepare the instructor debrief guide
                  </p>
                </div>
              </div>
              {debriefingError && (
                <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <div className="font-semibold">Generation failed</div>
                  <div>{debriefingError}</div>
                </div>
              )}
              <div className="ml-10">
                <button
                  onClick={handleGenerateDebriefingGuide}
                  disabled={generatingDebriefing}
                  className="mt-2 px-4 py-1.5 text-sm font-semibold bg-amber-500 text-white rounded-md hover:bg-amber-600 disabled:bg-amber-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {generatingDebriefing ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Generating Debriefing Guide... {debriefingProgress}%</span>
                    </>
                  ) : (
                    'Generate Debriefing Guide'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Debriefing Guide Card - Only shown when a debriefing guide actually exists */}
        {debriefingGuideStatus.exists && (
          <div className="mb-6">
            <div className="bg-amber-50/80 border-l-4 border-amber-500 rounded-lg p-6">
              <div className="p-4 bg-white rounded-lg border border-amber-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">📋</span>
                      <h4 className="font-semibold text-navy">Debriefing Preparation Report</h4>
                    </div>
                    <p className="text-sm text-steel">
                      Instructor preparation guide for conducting the CBTA debrief session
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-amber-700">
                        Status: {debriefingGuideStatus.status === 'finalized' ? '✅ Finalized' :
                             debriefingGuideStatus.status === 'edited' ? '✏️ Edited' :
                             debriefingGuideStatus.status === 'exported' ? '📄 Exported' :
                             '📝 Draft'}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/sessions/${session.id}/debriefing-guide`)}
                      className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-md transition-colors"
                    >
                      👁️ View
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Generate Assessment Button - Step 3 */}
        {/* Only show when there are completed transcripts (hides during first-time transcription) */}
        {hasCompletedTranscripts && selectedTranscriptId && !assessmentsGenerated.pilot && !assessmentsGenerated.instructor && (
          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              {/* Header row: icon + title */}
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-orca-elevate flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-navy leading-tight">
                    Step 3: Ready to Generate Assessment
                  </h3>
                  <p className="text-slate-700 text-xs">AI will analyze all available transcripts and create competency assessments</p>
                </div>
              </div>
              {/* Full-width content below header */}
              {assessmentError && (
                <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  <div className="font-semibold">Assessment generation failed</div>
                  <div>{assessmentError}</div>
                </div>
              )}
              <AssessmentGenerator
                sessionId={session.id}
                refreshKey={transcriptRefreshTrigger}
                onRunEventDetection={handleScrollToEventDetection}
                hideHeader
                transcriptVariant="white"
                onComplete={async (result) => {
                  console.log('[SessionDetails] Assessments generated:', {
                    pilot: result.assessments.length,
                    instructor: result.instructorAssessments.length,
                    pilotAvgScore: result.metadata.avgScore,
                    instructorAvgScore: result.metadata.instructorAvgScore,
                    spanBasedExtraction: result.metadata.spanBasedExtraction,
                  });
                  setAssessmentError(null);
                  loadAssessments();

                  if (result.metadata.spanBasedExtraction) {
                    console.log('[SessionDetails] Span-based extraction used - setting validation to verbatim');
                    setIsValidationVerbatim(true);
                  }

                  setJobTimelineRefreshKey(prev => prev + 1);
                }}
                onError={(error) => {
                  console.error('[SessionDetails] Assessment generation error:', error);
                  setAssessmentError(`Assessment generation failed: ${error.message}`);
                }}
              />
            </div>
          </div>
        )}

        {/* Assessment View/Edit Buttons */}
        {(assessmentsGenerated.pilot || assessmentsGenerated.instructor) && (
          <div className="mb-6">
            <div className="bg-orca-elevate/5 border-l-4 border-orca-elevate rounded-lg p-6">
              <h3 className="text-lg font-semibold text-navy mb-4">
                ✅ Assessments Generated
              </h3>

              <div className="space-y-3">
                {assessmentsGenerated.pilot && (
                  <div className="p-4 bg-white rounded-lg border border-steel/20 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-navy mb-1">Pilot Competency Assessment</h4>
                        <p className="text-sm text-steel">9 ICAO competencies evaluated</p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/sessions/${session.id}/edit-pilot-assessment`)}
                          className="px-4 py-2 text-sm font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors"
                        >
                          👁️ View
                        </button>
                      </div>
                    </div>

                    {/* Background Job Status Timeline */}
                    <BackgroundJobTimeline
                      sessionId={session.id}
                      assessmentType="pilot"
                      showTitle={true}
                      refreshKey={jobTimelineRefreshKey}
                      validationStatusOverride={isValidationVerbatim ? 'verbatim' : undefined}
                    />
                  </div>
                )}

                {assessmentsGenerated.instructor && (
                  <div className="p-4 bg-white rounded-lg border border-steel/20 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-navy mb-1">Instructor Effectiveness Assessment</h4>
                        <p className="text-sm text-steel">5 IEC criteria evaluated</p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/sessions/${session.id}/edit-instructor-assessment`)}
                          className="px-4 py-2 text-sm font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors"
                        >
                          👁️ View
                        </button>
                      </div>
                    </div>

                    {/* Background Job Status Timeline */}
                    <BackgroundJobTimeline
                      sessionId={session.id}
                      assessmentType="instructor"
                      showTitle={true}
                      validationStatusOverride={isValidationVerbatim ? 'verbatim' : undefined}
                      refreshKey={jobTimelineRefreshKey}
                    />
                  </div>
                )}

                {/* Delete Assessments Button - For Rapid Testing */}
                <div className="p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-navy mb-1">🗑️ Delete Assessments for Testing</h4>
                      <p className="text-sm text-steel mb-2">
                        Quickly delete all assessments to regenerate them for rapid testing.
                        Session, transcripts, and files will be preserved.
                      </p>
                    </div>
                    <button
                      onClick={handleDeleteAssessments}
                      disabled={isDeleting}
                      className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed rounded-md transition-colors whitespace-nowrap"
                    >
                      {isDeleting ? 'Deleting...' : '🗑️ Delete All Assessments'}
                    </button>
                  </div>
                  {deleteError && (
                    <div className="mt-2 text-sm text-red-600">
                      Error: {deleteError}
                    </div>
                  )}
                </div>

                {/* Generate Missing Assessment Type */}
                {(!assessmentsGenerated.pilot || !assessmentsGenerated.instructor) && (
                  <div className="p-4 bg-white rounded-lg border-2 border-dashed border-orca-elevate/40">
                    <h4 className="font-semibold text-navy mb-2">Generate Additional Assessment</h4>
                    <div className="flex gap-3">
                      {!assessmentsGenerated.pilot && (
                        <button
                          onClick={async () => {
                            try {
                              setAssessmentError(null);
                              setGeneratingPilotAssessment(true);
                              // Reconstruct transcript with speaker labels from speaker_segments
                              const { transcript: fullTranscript, totalDurationSeconds: totalDuration } =
                                await reconstructTranscriptWithSpeakerLabels(session.id);

                              // Get auth token for authorization
                              const { data: { session: authSession } } = await supabase.auth.getSession();
                              const authToken = authSession?.access_token;
                              if (!authToken) throw new Error('Not authenticated. Please log in and try again.');

                              const response = await fetch('/api/generate-assessment', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${authToken}`,
                                },
                                body: JSON.stringify({
                                  transcript: fullTranscript,
                                  sessionId: session.id,
                                  assessmentType: 'pilot',
                                  duration: `${Math.round(totalDuration / 60)} minutes`,
                                }),
                              });

                              if (response.ok) {
                                loadAssessments();
                                setJobTimelineRefreshKey(prev => prev + 1);
                              } else {
                                const errorData = await response.json();
                                setAssessmentError(errorData.message || errorData.error || 'Failed to generate pilot assessment');
                              }
                            } catch (err: any) {
                              setAssessmentError(err.message || 'Failed to generate pilot assessment');
                            } finally {
                              setGeneratingPilotAssessment(false);
                            }
                          }}
                          disabled={generatingPilotAssessment}
                          className="px-4 py-2 bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {generatingPilotAssessment ? (
                            <>
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Generating...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              Generate Pilot Assessments
                            </>
                          )}
                        </button>
                      )}
                      {!assessmentsGenerated.instructor && (
                        <button
                          onClick={async () => {
                            // Save to localStorage for persistence across navigation
                            localStorage.setItem(INSTRUCTOR_GEN_KEY, JSON.stringify({ startedAt: Date.now() }));

                            // Start progress simulation
                            setInstructorAssessmentProgress(0);
                            const progressInterval = setInterval(() => {
                              setInstructorAssessmentProgress(prev => {
                                if (prev < 90) return prev + 10;
                                return prev;
                              });
                            }, 3000);

                            try {
                              setAssessmentError(null);
                              setGeneratingInstructorAssessment(true);
                              setInstructorAssessmentProgress(10);

                              // Reconstruct transcript with speaker labels from speaker_segments
                              const { transcript: fullTranscript, totalDurationSeconds: totalDuration } =
                                await reconstructTranscriptWithSpeakerLabels(session.id);

                              // Get auth token for authorization
                              const { data: { session: instrAuthSession } } = await supabase.auth.getSession();
                              const instrAuthToken = instrAuthSession?.access_token;
                              if (!instrAuthToken) throw new Error('Not authenticated. Please log in and try again.');

                              const response = await fetch('/api/generate-assessment', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${instrAuthToken}`,
                                },
                                body: JSON.stringify({
                                  transcript: fullTranscript,
                                  sessionId: session.id,
                                  assessmentType: 'instructor',
                                  duration: `${Math.round(totalDuration / 60)} minutes`,
                                }),
                              });

                              if (response.ok) {
                                setInstructorAssessmentProgress(100);
                                // Check if response includes span-based extraction info
                                const result = await response.json();
                                if (result.metadata?.spanBasedExtraction) {
                                  setIsValidationVerbatim(true);
                                }
                                await loadAssessments();
                                setJobTimelineRefreshKey(prev => prev + 1);

                                // RCA/SMART now triggered server-side by generate-assessment API
                              } else {
                                const errorData = await response.json();
                                setAssessmentError(errorData.message || 'Failed to generate instructor assessment');
                              }
                            } catch (err: any) {
                              setAssessmentError(err.message || 'Failed to generate instructor assessment');
                            } finally {
                              clearInterval(progressInterval);
                              localStorage.removeItem(INSTRUCTOR_GEN_KEY);
                              setGeneratingInstructorAssessment(false);
                              setInstructorAssessmentProgress(0);
                            }
                          }}
                          disabled={generatingInstructorAssessment}
                          className="px-4 py-2 bg-sky text-white rounded-lg hover:bg-sky/90 transition-colors font-medium flex items-center gap-2 disabled:bg-sky/70 disabled:cursor-not-allowed"
                        >
                          {generatingInstructorAssessment ? (
                            <>
                              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Generating... {instructorAssessmentProgress}%
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                              Generate Instructor Assessment
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    {assessmentError && (
                      <div className="mt-2 text-sm text-red-600">
                        Error: {assessmentError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ===== Collapsible Sections ===== */}
      <div className="space-y-3 mb-6">

        {/* Mobile Workflow Banner - above Session Files */}
        <div className="p-4 bg-gradient-to-r from-navy to-navy/80 rounded-lg border border-navy/20">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-white mb-0.5">
                📱 Mobile Recording Workflow
              </h3>
              <p className="text-xs text-white/70">
                Step-by-step wizard: Record, transcribe, debrief, and assess — all from your phone.
              </p>
            </div>
            <Link
              to={`/session-workflow/${id}`}
              className="flex-shrink-0 px-4 py-2 text-sm font-semibold text-navy bg-white rounded-lg hover:bg-slate-100 transition-colors"
            >
              Open Workflow
            </Link>
          </div>
        </div>

        {/* Session Files (open initially) - AT TOP */}
        <CollapsibleSection
          title="Session Files"
          icon={<svg className="w-5 h-5 text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>}
          collapsed={collapsedSections.sessionFiles}
          onToggle={() => toggleSection('sessionFiles')}
          contentClassName="px-4 pb-4 border-t border-steel/10 pt-4"
        >
              {/* Upload Audio/Transcript - blue card (TOP) */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-start gap-3">
                  <span className="text-lg">🎙️</span>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-navy mb-0.5">
                      Upload Audio (MP3) or Transcript
                    </h3>
                    <p className="text-xs text-steel mb-3">
                      Accepted: Audio (MP3) and Transcripts (TXT, RTF, PDF, DOCX) - max 500MB
                    </p>

                    <div className="flex items-center gap-3 mb-3">
                      <button
                        onClick={() => setShowUpload(!showUpload)}
                        className="px-4 py-2 bg-orca-elevate text-white rounded-md hover:bg-orca-elevate/90 transition-colors text-sm font-medium inline-flex items-center gap-2 min-w-[200px] justify-center"
                      >
                        <span>📤</span>
                        <span>{showUpload ? 'Cancel Upload' : 'Choose File'}</span>
                      </button>

                      <div className="flex items-center gap-1.5">
                        <label htmlFor="cockpit-language" className="text-xs text-steel whitespace-nowrap">
                          Cockpit 2nd Language
                        </label>
                        <select
                          id="cockpit-language"
                          value={secondLanguage || ''}
                          onChange={(e) => {
                            const value = e.target.value || null;
                            setSecondLanguage(value);
                            // Persist to session record so it survives page refresh
                            if (id) {
                              supabase
                                .from('sessions')
                                .update({ cockpit_language: value })
                                .eq('id', id)
                                .then(({ error: saveErr }) => {
                                  if (saveErr) console.error('[SessionDetails] Failed to save cockpit_language:', saveErr);
                                });
                            }
                          }}
                          className="text-xs border border-blue-200 rounded px-2 py-1.5 bg-white text-navy focus:outline-none focus:ring-1 focus:ring-orca-elevate"
                        >
                          {COCKPIT_LANGUAGE_OPTIONS.map((opt) => (
                            <option key={opt.label} value={opt.code || ''}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Upload dropzone - only shown when showUpload is true */}
                {showUpload && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-steel/20">
                    <FileUpload onUploadComplete={handleUploadComplete} acceptedFileTypes="all" />
                  </div>
                )}
              </div>

              {/* Multi-Audio Transcription Section - right below Upload Audio */}
              {(() => {
                const audioFiles = getAudioFiles();

                // Show if there are 2+ audio files
                if (audioFiles.length >= 2) {
                  const orderedFiles = getOrderedAudioFiles();
                  const isCustomOrder = customFileOrder !== null;
                  // Collapse when files are already combined/transcribed
                  const alreadyProcessed = hasCompletedTranscripts;

                  const cardContent = (
                    <>
                          <h3 className="text-sm font-bold text-navy mb-1">
                            Multiple Audio Files Detected ({audioFiles.length} files)
                          </h3>
                          <p className="text-xs text-amber-800 mb-3">
                            These files will be combined in the order shown below. Use the arrows to reorder.
                          </p>

                          {/* Concatenation Order Preview with Sorting Controls */}
                          <div className="bg-white/80 rounded-md p-3 mb-3 border border-amber-200">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-gray-700">
                                Concatenation Order {isCustomOrder && <span className="text-amber-600">(custom)</span>}:
                              </p>
                              {isCustomOrder && (
                                <button
                                  onClick={resetFileOrder}
                                  className="text-xs text-amber-600 hover:text-amber-800 underline"
                                >
                                  Reset to auto
                                </button>
                              )}
                            </div>
                            <div className="space-y-1">
                              {orderedFiles.map((file, index) => (
                                <div key={file.id} className="flex items-center text-xs group">
                                  {/* Move buttons */}
                                  <div className="flex flex-col mr-1">
                                    <button
                                      onClick={() => moveFileUp(index)}
                                      disabled={index === 0}
                                      className="p-0.5 text-gray-400 hover:text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                      title="Move up"
                                    >
                                      ▲
                                    </button>
                                    <button
                                      onClick={() => moveFileDown(index)}
                                      disabled={index === orderedFiles.length - 1}
                                      className="p-0.5 text-gray-400 hover:text-amber-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                      title="Move down"
                                    >
                                      ▼
                                    </button>
                                  </div>
                                  <span className="w-5 h-5 flex items-center justify-center bg-amber-500 text-white rounded-full text-xs font-bold mr-2">
                                    {index + 1}
                                  </span>
                                  <span className="font-medium text-navy flex-1">{file.file_name}</span>
                                  <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs">
                                    {getAudioTypeLabel(file.file_name)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Concatenation Progress */}
                          {concatenationProgress && (
                            <div className="bg-white/80 rounded-md p-3 mb-3 border border-amber-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-gray-700">
                                  {concatenationProgress.stage === 'downloading' && `Downloading files (${concatenationProgress.fileIndex || 0}/${concatenationProgress.totalFiles || 0})...`}
                                  {concatenationProgress.stage === 'decoding' && 'Decoding audio...'}
                                  {concatenationProgress.stage === 'concatenating' && 'Combining audio files...'}
                                  {concatenationProgress.stage === 'encoding' && 'Encoding combined audio...'}
                                  {concatenationProgress.stage === 'uploading' && 'Uploading combined file...'}
                                  {concatenationProgress.stage === 'complete' && 'Concatenation complete!'}
                                </span>
                                <span className="text-xs font-bold text-amber-700">{concatenationProgress.progress}%</span>
                              </div>
                              <ProgressBar
                                progress={concatenationProgress.progress}
                                label=""
                                size="sm"
                                color="sky"
                                animated={concatenationProgress.stage !== 'complete'}
                                showPercentage={false}
                              />
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-2">
                            {/* Combine Only Button */}
                            <button
                              onClick={handleCombineOnly}
                              disabled={!!concatenationProgress || transcriptionStatus.status === 'processing'}
                              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-semibold inline-flex items-center space-x-2"
                            >
                              {concatenationProgress ? (
                                <>
                                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  <span>Combining...</span>
                                </>
                              ) : (
                                <>
                                  <span>🔗</span>
                                  <span>Combine Only</span>
                                </>
                              )}
                            </button>

                            {/* Combine & Transcribe Button */}
                            <button
                              onClick={handleTranscribeAll}
                              disabled={!!concatenationProgress || transcriptionStatus.status === 'processing'}
                              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-semibold inline-flex items-center space-x-2"
                            >
                              {concatenationProgress ? (
                                <>
                                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  <span>Combining Audio...</span>
                                </>
                              ) : transcriptionStatus.status === 'processing' ? (
                                <>
                                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  <span>Transcribing...</span>
                                </>
                              ) : (
                                <>
                                  <span>🎤</span>
                                  <span>Combine & Transcribe All</span>
                                </>
                              )}
                            </button>
                          </div>

                          <p className="text-xs text-amber-700 mt-2">
                            Tip: Name your files with "briefing", "session", or "debrief" for automatic ordering.
                          </p>
                    </>
                  );

                  // When already processed, show as a collapsed details element
                  if (alreadyProcessed) {
                    return (
                      <details className="mt-4 group">
                        <summary className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors text-sm font-medium text-amber-800 border border-amber-300">
                          <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span>🎵</span>
                          Re-combine audio files ({audioFiles.length} files)
                        </summary>
                        <div className="mt-2 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-300">
                          {cardContent}
                        </div>
                      </details>
                    );
                  }

                  // Default: show full prominent card
                  return (
                    <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border-2 border-amber-400">
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">🎵</span>
                        <div className="flex-1">
                          {cardContent}
                        </div>
                      </div>
                    </div>
                  );
                }

                return null;
              })()}

              {/* More Options: Record, Upload Text, Recordings — collapsed by default */}
              <details className="mt-4 group">
                <summary className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-lg bg-steel/5 hover:bg-steel/10 transition-colors text-sm font-medium text-steel">
                  <svg className="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  More options: Record audio, upload transcript, view recordings
                </summary>
                <div className="mt-3 space-y-4">
                  {/* Record Audio - blue-themed card */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">🎙️</span>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-navy mb-0.5">
                          Record Session Audio
                        </h3>
                        <p className="text-xs text-steel mb-3">
                          Record directly from your device microphone. Select the phase, then tap to start. Audio is saved in 30-second chunks for crash protection.
                        </p>
                        <div className="flex justify-start">
                          <RecordButton
                            sessionId={id!}
                            onRecordingComplete={() => {
                              loadTranscripts();
                              loadFiles();
                              loadRecordings();
                            }}
                            onError={(msg) => setError(msg)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Upload Pre-Transcribed Text File - light blue card */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">📄</span>
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-navy mb-0.5">
                          Upload Pre-Transcribed Text File
                        </h3>
                        <p className="text-xs text-steel mb-3">
                          Already have a transcript? Upload TXT, SRT, or VTT files here instead of transcribing audio.
                        </p>

                        <label className="inline-block cursor-pointer">
                          <span className="px-4 py-2 bg-orca-elevate text-white rounded-md hover:bg-orca-elevate/90 transition-colors text-sm font-medium inline-flex items-center gap-2">
                            {uploadingTranscript ? (
                              <>
                                <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span>Processing...</span>
                              </>
                            ) : (
                              <>
                                <span>📤</span>
                                <span>Choose Transcript File</span>
                              </>
                            )}
                          </span>
                          <input
                            type="file"
                            accept={getSupportedExtensions()}
                            onChange={handleManualTranscriptUpload}
                            className="hidden"
                            disabled={uploadingTranscript}
                          />
                        </label>
                        <p className="text-xs text-steel mt-2">
                          Supported: TXT, SRT, VTT
                        </p>

                        {transcriptUploadError && (
                          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                            ❌ {transcriptUploadError}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Recordings List — condensed */}
                  {recordings.length > 0 && (
                    <div className="space-y-1">
                      <h4 className="text-xs font-semibold text-steel uppercase tracking-wide">Recordings</h4>
                      {recordings.map((rec) => (
                        <div
                          key={`rec-more-${rec.id}`}
                          className="flex items-center justify-between px-2 py-1.5 border border-steel/10 rounded hover:bg-steel/5 transition-colors bg-white"
                        >
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <span className="text-sm">🎙️</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-navy leading-tight">
                                {phaseLabel(rec.phase)} Recording
                                <span className="text-steel font-normal ml-1.5">
                                  {rec.file_size_bytes ? formatFileSize(rec.file_size_bytes) : 'audio/webm'}
                                  {rec.duration_seconds ? ` • ${formatDuration(rec.duration_seconds)}` : ''}
                                  {' • '}{formatDateISO(rec.created_at)}
                                </span>
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1.5 ml-2">
                            {recordingStatusBadge(rec.status)}
                            {/\.(mp3|m4a|webm|wav|ogg|mp4|aac)$/i.test(rec.storage_path) && (
                              <button
                                onClick={() => handleToggleAudio(rec.id, 'session-recordings', rec.storage_path)}
                                className={`px-2 py-0.5 text-xs font-medium rounded transition-colors inline-flex items-center gap-1 ${
                                  playingAudioId === rec.id
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                    : 'text-orca-elevate hover:text-orca-elevate/80 border border-orca-elevate/30'
                                }`}
                              >
                                {playingAudioId === rec.id ? '⏹ Stop' : '▶ Play'}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                </div>
              </details>

              {/* Uploaded Files List — always visible */}
              {filesLoading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-orca-elevate"></div>
                  <p className="mt-1 text-xs text-steel">Loading files...</p>
                </div>
              ) : files.length > 0 ? (
                <div className="mt-4 space-y-1">
                  <h4 className="text-xs font-semibold text-steel uppercase tracking-wide">Uploaded Files</h4>
                  {files.map((file) => (
                    <div
                      key={`file-${file.id}`}
                      className="flex items-center justify-between px-2 py-1.5 border border-steel/10 rounded hover:bg-steel/5 transition-colors bg-white"
                    >
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <span className="text-sm">{getFileIcon(file.file_type)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-navy leading-tight truncate">
                            {file.file_name}
                            <span className="text-steel font-normal ml-1.5">
                              {formatFileSize(file.file_size)} • {file.file_type} • {formatDateISO(file.created_at)}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1.5 ml-2">
                        {/* Play button for audio */}
                        {file.file_type === 'audio' && (
                          <button
                            onClick={() => handleToggleAudio(file.id, 'session-files', file.storage_path)}
                            className={`px-2 py-0.5 text-xs font-medium rounded transition-colors inline-flex items-center gap-1 ${
                              playingAudioId === file.id
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-gray-100 text-navy hover:bg-gray-200'
                            }`}
                            title={playingAudioId === file.id ? 'Stop playback' : 'Play audio'}
                          >
                            {playingAudioId === file.id ? '⏹ Stop' : '▶ Play'}
                          </button>
                        )}
                        {/* Transcription/process status */}
                        {file.file_type === 'audio' && (
                          <>
                            {transcriptionStatus.status === 'processing' ? (
                              <div className="min-w-[100px]">
                                <ProgressBar progress={transcriptionStatus.progress} label="Transcribing..." size="sm" color="sky" animated={true} />
                              </div>
                            ) : transcripts.some(t => t.file_id === file.id && t.processing_status === 'completed') || (file.file_name.startsWith('combined_') && hasCompletedTranscripts) ? (
                              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-800 rounded-full">✓ Transcribed</span>
                            ) : (
                              <button onClick={() => handleTranscribe(file)} className="px-2 py-0.5 text-xs font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded transition-colors">🎤 Transcribe</button>
                            )}
                          </>
                        )}
                        {file.file_type === 'text' && (
                          <>
                            {(() => {
                              const linkedTranscript = transcripts.find(t => t.file_id === file.id);
                              if (linkedTranscript?.processing_status === 'completed') return <span className="px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-800 rounded-full">✓ Processed</span>;
                              if (linkedTranscript?.processing_status === 'pending') return <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-800 rounded-full">⏳ Pending</span>;
                              if (linkedTranscript?.processing_status === 'processing') return <span className="px-1.5 py-0.5 text-[10px] font-medium bg-yellow-100 text-yellow-800 rounded-full">⏳ Processing</span>;
                              return <button onClick={() => handleProcessTextFile(file)} className="px-2 py-0.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded transition-colors">📄 Process</button>;
                            })()}
                          </>
                        )}
                        <button onClick={() => handleDownload(file)} className="px-2 py-0.5 text-xs text-orca-elevate hover:text-orca-elevate/80 transition-colors">Download</button>
                        <button onClick={() => handleDeleteFile(file)} className="px-2 py-0.5 text-xs text-red-600 hover:text-red-700 transition-colors">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 text-center py-4 bg-white rounded-lg border border-steel/20">
                  <p className="text-steel text-sm">No files uploaded yet</p>
                  <p className="text-xs text-steel/70 mt-1">Upload audio or transcript files above</p>
                </div>
              )}

              {/* Combine & Re-transcribe Proposal Banner */}
          {showCombineProposal && (
            <div className="mb-6 bg-gradient-to-r from-sky-50 to-blue-50 border-2 border-sky-400 rounded-lg p-5">
              <div className="flex items-start gap-4">
                <span className="text-2xl mt-0.5">🔄</span>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-navy mb-1.5">
                    New audio file uploaded &mdash; Combine all recordings?
                  </h3>
                  <p className="text-sm text-steel mb-3 leading-relaxed">
                    Combining all audio files into one recording and re-transcribing gives
                    better speaker diarization accuracy. The existing transcripts will be
                    archived (hidden but preserved).
                  </p>
                  {hasVoicePassport && (
                    <p className="text-sm text-sky-700 mb-3 flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-sky-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Using existing speaker profiles for better accuracy. No need to re-assign roles.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-4">
                    <button
                      onClick={handleCombineAndRetranscribe}
                      disabled={!!concatenationProgress || transcriptionStatus.status === 'processing'}
                      className="px-5 py-2.5 text-sm font-semibold bg-sky-600 text-white rounded-md hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                    >
                      ✅ Combine All &amp; Re-transcribe
                    </button>
                    <button
                      onClick={() => setShowCombineProposal(false)}
                      className="px-5 py-2.5 text-sm font-semibold text-steel hover:text-navy border border-steel/30 rounded-md hover:border-steel/50 transition-colors"
                    >
                      Skip for now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Debriefing Stitch Proposal Banner */}
          {showDebriefStitchProposal && latestAudioFile && (
            <div className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-400 rounded-lg p-5">
              <div className="flex items-start gap-4">
                <span className="text-2xl mt-0.5">🧵</span>
                <div className="flex-1">
                  <h3 className="text-base font-bold text-navy mb-1.5">
                    Debriefing audio detected &mdash; Transcribe &amp; stitch?
                  </h3>
                  <p className="text-sm text-steel mb-3 leading-relaxed">
                    This will transcribe <strong>{latestAudioFile.file_name}</strong> separately
                    and stitch it with the existing transcript. Speaker profiles will be
                    used for consistent identification. The result becomes the authoritative transcript.
                  </p>
                  {hasVoicePassport && (
                    <p className="text-sm text-emerald-700 mb-3 flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Speaker profiles available — consistent identification across phases.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-4">
                    <button
                      onClick={handleDebriefTranscribe}
                      disabled={transcriptionStatus.status === 'processing'}
                      className="px-5 py-2.5 text-sm font-semibold bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                    >
                      Transcribe &amp; Stitch
                    </button>
                    <button
                      onClick={handleCombineAndRetranscribe}
                      disabled={!!concatenationProgress || transcriptionStatus.status === 'processing'}
                      className="px-5 py-2.5 text-sm font-semibold text-steel hover:text-navy border border-steel/30 rounded-md hover:border-steel/50 transition-colors"
                    >
                      Combine All Instead
                    </button>
                    <button
                      onClick={() => setShowDebriefStitchProposal(false)}
                      className="px-5 py-2.5 text-sm font-semibold text-steel/60 hover:text-steel transition-colors"
                    >
                      Skip
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </CollapsibleSection>

        {/* Session Details (with Participants) - BELOW Session Files */}
        <CollapsibleSection
          title="Session Details"
          icon={<svg className="w-5 h-5 text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
          collapsed={collapsedSections.sessionDetails}
          onToggle={() => toggleSection('sessionDetails')}
          contentClassName="px-6 pb-6 border-t border-steel/10 pt-4 space-y-6"
        >
              {/* Session Info Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-steel mb-1">Session Date</p>
                  <p className="text-sm font-semibold text-navy">{formatDateISO(session.session_date)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-steel mb-1">Flight Phase</p>
                  <p className="text-sm font-semibold text-navy">{session.flight_phase}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-steel mb-1">Aircraft Type</p>
                  <p className="text-sm font-semibold text-navy">{session.aircraft_type}</p>
                </div>
              </div>

              {/* Participants Row */}
              <div>
                <h3 className="text-sm font-semibold text-navy mb-3">Participants</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-steel mb-1">Captain</p>
                    <p className="text-sm font-semibold text-navy">
                      {pilotCrew.find(m => m.crew_role === 'captain')?.full_name || 'Not assigned'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-steel mb-1">First Officer (Trainee)</p>
                    <p className="text-sm font-semibold text-navy">{session.trainee?.full_name || pilotCrew.find(m => m.crew_role === 'first_officer')?.full_name || 'Not assigned'}</p>
                    <p className="text-xs text-steel">{session.trainee?.email || ''}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-steel mb-1">Instructor</p>
                    <p className="text-sm font-semibold text-navy">{session.instructor.full_name}</p>
                  </div>
                </div>
              </div>

              {/* Optional Scenario */}
              {session.scenario && (
                <div>
                  <h3 className="text-sm font-semibold text-navy mb-2">Scenario Description</h3>
                  <div className="border border-steel/20 rounded-lg p-4 bg-ice">
                    <p className="text-navy whitespace-pre-wrap">{session.scenario}</p>
                  </div>
                </div>
              )}

              {/* Optional Instructor Notes */}
              {session.instructor_notes && (
                <div>
                  <h3 className="text-sm font-semibold text-navy mb-2">Instructor Notes</h3>
                  <div className="border border-steel/20 rounded-lg p-4 bg-ice">
                    <p className="text-navy whitespace-pre-wrap">{session.instructor_notes}</p>
                  </div>
                </div>
              )}
        </CollapsibleSection>

        {/* Event Detection (collapsible, secondary information) */}
        {transcripts.length > 0 && selectedTranscriptId && session && (
          <CollapsibleSection
            title="Event Detection"
            icon={<svg className="w-5 h-5 text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>}
            collapsed={collapsedSections.eventDetection}
            onToggle={() => toggleSection('eventDetection')}
            contentClassName="px-6 pb-6 border-t border-steel/10 pt-4 transition-all duration-300"
            contentRef={eventDetectionRef}
          >
                <EventDetectionPanel
                  sessionId={session.id}
                  transcriptId={selectedTranscriptId}
                  aircraftType={session.aircraft_type}
                  flightPhase={session.flight_phase}
                  scenario={session.scenario || undefined}
                  onComplete={() => {
                    console.log('[SessionDetails] Event detection completed');
                    setTranscriptRefreshTrigger((prev) => prev + 1);
                  }}
                />
          </CollapsibleSection>
        )}

        {/* Available Transcripts */}
        <CollapsibleSection
          title={`Available Transcripts (${transcripts.filter(t => !t.is_archived).length})`}
          icon={<svg className="w-5 h-5 text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
          collapsed={collapsedSections.availableTranscripts}
          onToggle={() => toggleSection('availableTranscripts')}
        >
              <TranscriptList
                sessionId={session.id}
                onViewTranscript={openTranscriptViewer}
                onTranscriptSelect={handleTranscriptSelect}
                onProcessTranscript={handleProcessPendingTranscript}
                onCombineTranscripts={handleCombineTranscripts}
                selectedTranscriptId={selectedTranscriptId || undefined}
                refreshTrigger={transcriptRefreshTrigger}
                processingTranscriptId={processingTranscriptId || undefined}
              />
        </CollapsibleSection>

        {/* Progressive Transcription Status */}
        {session && progressive.jobs.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-sky-500">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-heading font-semibold text-navy">
                Progressive Transcription
              </h3>
              {progressive.isComplete && (
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700 border border-green-300">
                  READY
                </span>
              )}
              {progressive.isProcessing && (
                <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-sky-100 text-sky-700 border border-sky-300">
                  <span className="inline-block w-2 h-2 bg-sky-500 rounded-full animate-pulse" />
                  PROCESSING
                </span>
              )}
            </div>
            <div className="space-y-2">
              {progressive.jobs.map((job) => (
                <div key={job.id} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 capitalize">
                    {job.phase.replace(/_/g, ' ')}
                  </span>
                  <span
                    className={
                      job.status === 'completed'
                        ? 'text-green-700 font-medium'
                        : job.status === 'failed'
                        ? 'text-red-600 font-medium'
                        : job.status === 'processing' || job.status === 'stitching'
                        ? 'text-sky-700'
                        : 'text-slate-500'
                    }
                  >
                    {job.status === 'completed' ? 'Done' : job.status === 'failed' ? 'Failed' : job.status === 'processing' ? 'Transcribing...' : job.status === 'stitching' ? 'Stitching...' : job.status === 'pending' ? 'Queued' : job.status}
                  </span>
                </div>
              ))}
            </div>
            {progressive.jobs.some(j => j.status === 'failed') && (
              <p className="mt-2 text-xs text-red-600">
                {progressive.jobs.find(j => j.status === 'failed')?.errorMessage}
              </p>
            )}
          </div>
        )}

        {/* Processing Status */}
        {session && (
          <CollapsibleSection
            title="Processing Status"
            icon={<svg className="w-5 h-5 text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            collapsed={collapsedSections.processingStatus}
            onToggle={() => toggleSection('processingStatus')}
          >
                <CompactProcessingStatus
                  key={`processing-${session.id}-${transcriptionStatus.status}`}
                  sessionId={session.id}
                  onComplete={() => {
                    loadTranscripts();
                    loadFiles();
                  }}
                />
          </CollapsibleSection>
        )}
      </div>

      {/* Metadata & Actions */}
      <div className="border-t border-steel/20 pt-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-steel">
            Created on {formatDateISO(session.created_at)}
          </p>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/sessions')}
              className="px-4 py-2 text-sm font-medium text-steel hover:text-navy border border-steel/30 rounded-md transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => navigate('/sessions')}
              className="btn-primary"
            >
              Back to Sessions
            </button>
          </div>
        </div>
      </div>
        </div>
      </div>

      {/* Enhanced Transcript Viewer */}
      {showTranscriptViewer && selectedTranscriptId && (
        <TranscriptViewer
          transcriptId={selectedTranscriptId}
          sessionId={session.id}
          onClose={() => setShowTranscriptViewer(false)}
          sessionInfo={{
            trainee_name: session.trainee?.full_name || pilotCrew.find(m => m.crew_role === 'first_officer')?.full_name || 'Trainee',
            session_date: formatDateISO(session.session_date),
          }}
        />
      )}

      {/* Phase 2: Speaker Role Assignment Modal */}
      <SpeakerRoleAssignmentModal
        sessionId={session.id}
        instructorId={session.instructor_id}
        captainId={pilotCrew.find((member) => member.crew_role === 'captain')?.user_id}
        firstOfficerId={pilotCrew.find((member) => member.crew_role === 'first_officer')?.user_id}
        isOpen={showSpeakerRoleModal}
        onClose={() => setShowSpeakerRoleModal(false)}
        onComplete={(assignments) => {
          console.log('[SessionDetails] Speaker roles assigned:', assignments);
          setShowSpeakerRoleModal(false);
          loadSpeakerRoles(); // Reload speaker roles to update UI
        }}
      />
    </div>
  );
}
